import { useState, useRef, useEffect, useCallback } from "react";
import { supabase } from './supabase';
import BarcodeScanner from './BarcodeScanner';
import AdminPage from './AdminPage';

// ─────────────────────────────────────────────────────────────
// 300+ INDIAN PRODUCTS DATABASE
// ─────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────
// API CALLS
// ─────────────────────────────────────────────────────────────

// Robustly extract and repair JSON from a raw string

function normalizeHealthScore(obj) {
  if (obj && typeof obj.healthScore === 'number') {
    if (obj.healthScore > 10) {
      obj.healthScore = obj.healthScore / 10;
    }
    obj.healthScore = Math.round(obj.healthScore * 10) / 10;
  }
  return obj;
}

function extractJSON(txt) {
  // Strip markdown fences
  txt = txt.replace(/```json\s*/gi, "").replace(/```\s*/g, "").trim();

  // Find the outermost { ... }
  const start = txt.indexOf("{");
  if (start === -1) throw new Error("No JSON object found in response");

  // Walk forward tracking depth to find the matching closing brace
  let depth = 0, end = -1;
  for (let i = start; i < txt.length; i++) {
    const ch = txt[i];
    // Skip escaped chars inside strings
    if (ch === "\\" ) { i++; continue; }
    if (ch === '"') {
      // skip string content
      i++;
      while (i < txt.length && txt[i] !== '"') {
        if (txt[i] === "\\") i++;
        i++;
      }
      continue;
    }
    if (ch === "{") depth++;
    else if (ch === "}") { depth--; if (depth === 0) { end = i; break; } }
  }

  const raw = end !== -1 ? txt.slice(start, end + 1) : txt.slice(start);

  // Attempt clean parse first
  try { return JSON.parse(raw); } catch (_) {}

  // Repair common issues:
  // 1. Remove trailing commas before } or ]
  let fixed = raw.replace(/,\s*([\]}])/g, "$1");
  // 2. Replace single quotes with double quotes for keys/values (careful)
  // 3. Truncated arrays – close any open arrays/objects
  const opens = (fixed.match(/\[/g) || []).length;
  const closes = (fixed.match(/\]/g) || []).length;
  const openObj = (fixed.match(/\{/g) || []).length;
  const closeObj = (fixed.match(/\}/g) || []).length;
  // Remove the last incomplete item in an array (item not followed by , or ])
  fixed = fixed.replace(/,\s*$/, ""); // trailing comma at end
  for (let i = 0; i < opens - closes; i++) fixed += "]";
  for (let i = 0; i < openObj - closeObj; i++) fixed += "}";

  try { return JSON.parse(fixed); } catch (e2) {
    throw new Error("Could not parse response. Please try again.");
  }
}

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";


// Keep Render backend alive
setInterval(() => {
  fetch(`${API_URL}/health`).catch(() => {});
}, 10 * 60 * 1000); // ping every 10 minutes

async function callClaude(prompt, sys = "", tokens = 1500, useSearch = false, cacheKey = null) {
  // Check cache first for full analysis calls
  if (tokens > 900 && cacheKey) {
    try {
      const { data: cached } = await supabase
        .from('product_cache')
        .select('analysis')
        .eq('product_key', cacheKey)
        .single();
      if (cached?.analysis) {
        console.log('✓ Cache hit:', cacheKey);
        return cached.analysis;
      }
    } catch (_) {}
  }

  const res = await fetch(`${API_URL}/api/analyze`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt, sys, tokens, useSearch, cacheKey })
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`Server error ${res.status}: ${errText.slice(0, 100)}`);
  }
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  const result = extractJSON(data.text);

  // Save to cache
  if (tokens > 900 && cacheKey && result?.name) {
    try {
      await supabase.from('product_cache').upsert({
        product_key: cacheKey,
        analysis: result
      });
      console.log('✓ Saved to cache:', cacheKey);
    } catch (_) {}
  }

  // Save to history if user is logged in
  if (result?.name) {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        await supabase.from('analysis_history').insert({
          user_id: authUser.id,
          product_name: result.name,
          product_emoji: result.emoji || "🍱",
          health_score: result.healthScore || null,
          analysis: result
        });
      }
    } catch (_) {}
  }

  return result;
}


// ─────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Plus Jakarta Sans',sans-serif;background:#ffffff;color:#0d1f0f;min-height:100vh}
:root{
  --g1:#16a34a;--g2:#15803d;--g3:#14532d;
  --acc:#f59e0b;--red:#dc2626;--blue:#2563eb;
  --bg:#ffffff;--card:#fff;--border:#f0f0f0;
  --muted:#6b7280;--text:#0d1f0f;
  --sh:0 1px 4px rgba(0,0,0,.06),0 4px 16px rgba(0,0,0,.06);
  --sh2:0 8px 32px rgba(0,0,0,.10),0 2px 8px rgba(0,0,0,.06);
  --r:16px;--r2:12px
}
@keyframes spin{to{transform:rotate(360deg)}}
/* Mobile bottom nav */
.bottom-nav{display:none;position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid var(--border);z-index:999;padding:6px 0 env(safe-area-inset-bottom);box-shadow:0 -2px 16px rgba(0,0,0,0.08)}
.bottom-nav-item{flex:1;display:flex;flex-direction:column;align-items:center;gap:2px;padding:6px 4px;border:none;background:none;cursor:pointer;font-family:inherit;color:var(--muted);font-size:10px;font-weight:600;transition:all .15s;min-width:0}
.bottom-nav-item.active{color:var(--g1)}
.bottom-nav-item.active .bn-icon{transform:scale(1.15)}
.bottom-nav-item .bn-icon{font-size:22px;line-height:1;transition:transform .15s}
/* Hide nav links on mobile, show hamburger */
@media(max-width:768px){
  .nav-links{display:none!important}
  .hamburger{display:flex!important}
  .bottom-nav{display:flex!important}
  .nav-right span:first-child{display:none!important}
  .navbar{padding:0 12px!important;height:52px}
  body{padding-bottom:72px}
  .nav-logo-text{font-size:15px!important}
}
@media(min-width:769px){
  .bottom-nav{display:none!important}
  .hamburger{display:none!important}
}
@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes slideDown{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
.fade-up{animation:fadeUp .3s ease}
.slide-down{animation:slideDown .2s ease}
.spin{animation:spin .7s linear infinite}
/* Scrollbar */
::-webkit-scrollbar{width:5px;height:5px}
::-webkit-scrollbar-track{background:#f1f1f1}
::-webkit-scrollbar-thumb{background:#c4d4c6;border-radius:10px}
/* Nav */
.navbar{background:rgba(255,255,255,0.95);border-bottom:1px solid #f5f5f5;height:58px;display:flex;align-items:center;padding:0 24px;position:sticky;top:0;z-index:500;box-shadow:0 2px 20px rgba(0,0,0,.06);backdrop-filter:blur(12px)}
.nav-logo{display:flex;align-items:center;gap:9px;cursor:pointer}
.nav-logo-box{width:32px;height:32px;background:linear-gradient(135deg,var(--g1),#22c55e);border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px;box-shadow:0 3px 10px rgba(22,163,74,.3)}
.nav-logo-text{font-weight:900;font-size:17px;color:var(--g2);letter-spacing:-.5px}
.nav-links{display:flex;gap:4px;margin-left:28px}
.nav-link{padding:7px 13px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;border:none;background:none;color:var(--muted);font-family:inherit;transition:all .15s}
.nav-link:hover,.nav-link.active{background:#f0fdf4;color:var(--g1)}
.nav-right{margin-left:auto;display:flex;align-items:center;gap:8px}
.btn-outline{padding:7px 16px;border:1.5px solid var(--border);border-radius:8px;font-size:13px;font-weight:700;background:#fff;color:var(--text);cursor:pointer;font-family:inherit;transition:all .15s}
.btn-outline:hover{border-color:var(--g1);color:var(--g1)}
.btn-green{padding:7px 16px;border:none;border-radius:8px;font-size:13px;font-weight:700;background:linear-gradient(135deg,var(--g1),var(--g2));color:#fff;cursor:pointer;font-family:inherit;transition:all .15s}
.btn-green:hover{transform:translateY(-1px);box-shadow:0 4px 14px rgba(22,163,74,.35)}
/* Modal */
.modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:1000;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(3px);padding:16px}
.modal{background:#fff;border-radius:18px;padding:28px;width:100%;max-width:420px;box-shadow:var(--sh2);animation:fadeUp .25s ease}
.modal-title{font-size:20px;font-weight:800;margin-bottom:4px}
.modal-sub{font-size:13px;color:var(--muted);margin-bottom:22px}
/* Cards */
.card{background:var(--card);border-radius:var(--r);box-shadow:var(--sh);padding:22px}
.card-sm{background:var(--card);border-radius:var(--r2);box-shadow:var(--sh);padding:16px}
/* Inputs */
.inp{width:100%;padding:11px 14px;border:1.5px solid var(--border);border-radius:var(--r2);font-size:14px;font-family:inherit;outline:none;color:var(--text);background:#fff;transition:border .15s}
.inp:focus{border-color:var(--g1);box-shadow:0 0 0 3px rgba(22,163,74,.1)}
.inp::placeholder{color:#9ca3af}
.inp-label{font-size:12px;font-weight:700;color:var(--muted);margin-bottom:5px;text-transform:uppercase;letter-spacing:.5px}
.inp-group{margin-bottom:14px}
.sel{width:100%;padding:11px 14px;border:1.5px solid var(--border);border-radius:var(--r2);font-size:14px;font-family:inherit;outline:none;color:var(--text);background:#fff;transition:border .15s;appearance:none;-webkit-appearance:none}
.sel:focus{border-color:var(--g1)}
/* Badges */
.badge{display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:20px;font-size:11px;font-weight:700}
.badge-green{background:#f0fdf4;color:var(--g1);border:1px solid #bbf7d0}
.badge-yellow{background:#fffbeb;color:#92400e;border:1px solid #fde68a}
.badge-red{background:#fef2f2;color:var(--red);border:1px solid #fecaca}
.badge-blue{background:#eff6ff;color:var(--blue);border:1px solid #bfdbfe}
.badge-gray{background:#f9fafb;color:#6b7280;border:1px solid #e5e7eb}
/* Search hero */
.hero{background:linear-gradient(140deg,var(--g3) 0%,var(--g2) 60%,var(--g1) 100%);border-radius:18px;padding:28px 24px 120px;position:relative;overflow:hidden;margin-bottom:20px;max-width:100%}
.hero::before{content:'';position:absolute;top:-60px;right:-60px;width:240px;height:240px;background:rgba(255,255,255,.05);border-radius:50%;pointer-events:none}
.hero::after{content:'';position:absolute;bottom:-40px;right:40px;width:140px;height:140px;background:rgba(255,255,255,.04);border-radius:50%;pointer-events:none}
.hero-title{color:#fff;font-size:22px;font-weight:900;margin-bottom:4px}
.hero-sub{color:rgba(255,255,255,.65);font-size:13px;margin-bottom:20px}
/* Search bar */
.search-wrap{position:relative;z-index:50}
.search-box{display:flex;gap:0;margin-bottom:8px;position:relative}
.search-inp{flex:1;padding:13px 56px 13px 16px;font-size:15px;font-family:inherit;border-radius:11px;border:2px solid transparent;outline:none;background:#fff;color:#0d1f0f;box-shadow:0 4px 20px rgba(0,0,0,.2);transition:box-shadow .2s;width:100%}
.search-inp:focus{box-shadow:0 0 0 3px rgba(255,255,255,.25),0 4px 20px rgba(0,0,0,.2)}
.search-inp::placeholder{color:#9ca3af}
.btn-analyze{padding:0 18px;background:var(--acc);border:none;border-radius:0 11px 11px 0;color:#fff;font-weight:800;font-size:14px;cursor:pointer;font-family:inherit;white-space:nowrap;transition:all .15s;height:100%;position:absolute;right:0;top:0;bottom:0}
.btn-analyze:hover{background:#d97706;transform:translateY(-1px)}
.url-row{display:flex;gap:8px}
.url-inp{flex:1;padding:10px 13px;font-size:13px;font-family:inherit;border-radius:10px;border:1px solid rgba(255,255,255,.22);outline:none;background:rgba(255,255,255,.13);color:#fff;transition:background .15s}
.url-inp:focus{background:rgba(255,255,255,.2)}
.url-inp::placeholder{color:rgba(255,255,255,.5)}
.btn-url{padding:10px 14px;background:rgba(255,255,255,.18);border:1px solid rgba(255,255,255,.28);border-radius:10px;color:#fff;font-weight:700;font-size:13px;cursor:pointer;font-family:inherit;transition:background .15s}
.btn-url:hover{background:rgba(255,255,255,.28)}
/* Dropdown */
.drop{position:absolute;top:calc(100% + 5px);left:0;right:0;background:#fff;border-radius:13px;box-shadow:0 12px 48px rgba(0,0,0,.22);border:1px solid var(--border);z-index:100;overflow-y:auto;max-height:400px}
.drop-header{padding:8px 14px 4px;font-size:10px;color:#9ca3af;font-weight:700;text-transform:uppercase;letter-spacing:1px;border-bottom:1px solid #f3f4f6;background:#fafafa}
.drop-row{padding:10px 14px;display:flex;align-items:center;gap:11px;border-bottom:1px solid #f9fafb;cursor:pointer;transition:background .1s}
.drop-row:last-child{border-bottom:none}
.drop-row:hover{background:#f0fdf4}
.drop-cat{font-size:10px;color:#9ca3af;font-weight:700;padding:6px 14px 2px;background:#fafafa;border-bottom:1px solid #f3f4f6;text-transform:uppercase;letter-spacing:.8px}
/* Results */
.result-header{display:flex;gap:18px;align-items:flex-start;flex-wrap:wrap}
.score-ring{width:76px;height:76px;border-radius:50%;border:5px solid;display:flex;flex-direction:column;align-items:center;justify-content:center;flex-shrink:0}
.ing-row{display:flex;align-items:flex-start;gap:12px;padding:11px 0;border-bottom:1px solid #f3f4f6}
.ing-row:last-child{border-bottom:none}
.ing-dot{width:9px;height:9px;border-radius:50%;margin-top:5px;flex-shrink:0}
.harmful-block{background:#fef2f2;border:1px solid #fecaca;border-radius:11px;padding:13px 15px;margin-bottom:9px}
.alt-card{display:flex;align-items:center;gap:14px;padding:13px 14px;border-radius:12px;border:1.5px solid var(--border);margin-bottom:9px;cursor:pointer;transition:all .18s}
.alt-card:last-child{margin-bottom:0}
.alt-card:hover{border-color:var(--g1);background:#f0fdf4;transform:translateX(3px)}
/* Tabs */
.tabs{display:flex;gap:3px;background:#edf1ed;border-radius:11px;padding:4px;margin-bottom:20px}
.tab{flex:1;padding:9px;text-align:center;border-radius:8px;cursor:pointer;font-size:13px;font-weight:600;border:none;background:none;color:var(--muted);font-family:inherit;transition:all .15s}
.tab.active{background:#fff;color:var(--g1);font-weight:800;box-shadow:var(--sh)}
/* Calorie sections */
.macro-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:14px 0}
.macro-box{background:var(--bg);border-radius:10px;padding:13px;text-align:center}
.macro-val{font-size:22px;font-weight:900}
.macro-lbl{font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:.8px;margin-top:2px}
.food-item{display:flex;align-items:center;gap:12px;padding:11px 12px;background:var(--bg);border-radius:10px;margin-bottom:7px}
.prog-wrap{margin-bottom:12px}
.prog-label{display:flex;justify-content:space-between;font-size:13px;font-weight:600;margin-bottom:5px}
.prog-bar{height:9px;background:#e9f0ea;border-radius:5px;overflow:hidden}
.prog-fill{height:100%;border-radius:5px;transition:width .8s cubic-bezier(.4,0,.2,1)}
/* BMR */
.calc-grid{display:grid;grid-template-columns:1fr 1fr;gap:13px}
.result-boxes{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-top:18px}
.r-box{padding:15px;border-radius:11px;text-align:center}
.r-val{font-size:22px;font-weight:900}
.r-lbl{font-size:10px;text-transform:uppercase;letter-spacing:.8px;margin-top:2px}
/* Workout */
.activity-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin:14px 0}
.activity-card{padding:14px 12px;border-radius:12px;border:1.5px solid var(--border);cursor:pointer;text-align:center;transition:all .15s}
.activity-card:hover,.activity-card.sel{border-color:var(--g1);background:#f0fdf4}
.activity-card.sel{border-width:2px}
/* Nutrition */
.nut-table{width:100%;border-collapse:collapse;margin-top:8px}
.nut-table th{text-align:left;font-size:11px;font-weight:700;color:var(--muted);padding:8px 10px;border-bottom:2px solid var(--border);text-transform:uppercase;letter-spacing:.8px}
.nut-table td{padding:9px 10px;font-size:14px;border-bottom:1px solid #f3f4f6}
.nut-table tr:last-child td{border-bottom:none}
.nut-table .nut-name{font-weight:600}
/* misc */
.divider{height:1px;background:var(--border);margin:16px 0}
.section-title{font-size:15px;font-weight:800;color:var(--text);margin-bottom:14px;display:flex;align-items:center;gap:7px}
.empty{text-align:center;padding:48px 20px}
.empty-icon{font-size:50px;margin-bottom:14px}
.chip{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;background:var(--bg);border-radius:20px;font-size:12px;font-weight:600;color:var(--muted)}
.quick-pill{padding:8px 14px;background:#f9fafb;border:1.5px solid var(--border);border-radius:22px;font-size:13px;font-weight:600;cursor:pointer;color:#374151;font-family:inherit;transition:all .15s}
.quick-pill:hover{background:#f0fdf4;border-color:var(--g1);color:var(--g2)}
.btn-full{width:100%;padding:13px;background:linear-gradient(135deg,var(--g1),var(--g2));border:none;border-radius:var(--r2);color:#fff;font-weight:800;font-size:15px;cursor:pointer;font-family:inherit;transition:all .15s;margin-top:4px}
.btn-full:hover{transform:translateY(-2px);box-shadow:0 6px 20px rgba(22,163,74,.3)}
.error-box{background:#fef2f2;border:1px solid #fecaca;border-radius:11px;padding:14px;color:var(--red);font-size:14px;font-weight:500;margin-bottom:14px}
.loading-box{background:#fff;border-radius:var(--r);padding:36px 24px;text-align:center;box-shadow:var(--sh)}
.user-avatar{width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,var(--g1),var(--acc));display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:13px;cursor:pointer}
@media(max-width:640px){
  .calc-grid{grid-template-columns:1fr}
  .result-boxes{grid-template-columns:1fr 1fr}
  .macro-grid{grid-template-columns:1fr 1fr}
  .activity-grid{grid-template-columns:1fr 1fr}
  .nav-links{display:none}
}
/* ── SIDEBAR DRAWER ─────────────────────── */
@keyframes slideRight{from{transform:translateX(-100%);opacity:0}to{transform:translateX(0);opacity:1}}
@keyframes slideLeft{from{transform:translateX(0);opacity:1}to{transform:translateX(-100%);opacity:0}}
.sidebar-overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:800;backdrop-filter:blur(2px);animation:fadeUp .2s ease}
.sidebar-drawer{position:fixed;left:0;top:0;bottom:0;width:280px;background:#fff;z-index:900;box-shadow:4px 0 32px rgba(0,0,0,.14);display:flex;flex-direction:column;animation:slideRight .25s cubic-bezier(.4,0,.2,1)}
.drawer-head{padding:20px 20px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between}
.drawer-logo{display:flex;align-items:center;gap:9px}
.drawer-close{width:32px;height:32px;border-radius:8px;border:none;background:var(--bg);cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;color:var(--muted);transition:all .15s}
.drawer-close:hover{background:#fee2e2;color:var(--red)}
.drawer-body{flex:1;overflow-y:auto;padding:12px 10px}
.drawer-section{margin-bottom:6px}
.drawer-section-btn{width:100%;display:flex;align-items:center;justify-content:space-between;padding:11px 12px;border-radius:10px;border:none;background:none;cursor:pointer;font-family:inherit;font-size:14px;font-weight:700;color:var(--text);transition:all .15s}
.drawer-section-btn:hover{background:var(--bg)}
.drawer-section-btn.open{background:#f0fdf4;color:var(--g1)}
.drawer-section-icon{font-size:18px;margin-right:8px}
.drawer-chevron{font-size:11px;color:var(--muted);transition:transform .2s}
.drawer-chevron.open{transform:rotate(90deg)}
.drawer-sub{padding:4px 0 4px 12px}
.drawer-sub-item{width:100%;display:flex;align-items:center;gap:10px;padding:9px 12px;border-radius:8px;border:none;background:none;cursor:pointer;font-family:inherit;font-size:13px;font-weight:600;color:var(--muted);transition:all .15s;text-align:left}
.drawer-sub-item:hover,.drawer-sub-item.active{background:#f0fdf4;color:var(--g1)}
.drawer-sub-item .sub-count{margin-left:auto;background:var(--bg);color:var(--muted);font-size:10px;font-weight:700;padding:2px 7px;border-radius:20px}
.drawer-divider{height:1px;background:var(--border);margin:10px 12px}
.drawer-nav-item{width:100%;display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:10px;border:none;background:none;cursor:pointer;font-family:inherit;font-size:13px;font-weight:700;color:var(--muted);transition:all .15s;text-align:left}
.drawer-nav-item:hover,.drawer-nav-item.active{background:#f0fdf4;color:var(--g1)}
/* hamburger btn */
.hamburger{width:36px;height:36px;border-radius:9px;border:1.5px solid var(--border);background:#fff;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;transition:all .15s}
.hamburger:hover{border-color:var(--g1);background:#f0fdf4}
.ham-line{width:16px;height:2px;background:var(--text);border-radius:2px;transition:all .2s}
/* ── CATEGORIES PAGE ─────────────────────── */
.cat-hero{background:linear-gradient(135deg,var(--g3),var(--g2));border-radius:18px;padding:28px 24px;margin-bottom:22px;color:#fff;position:relative;overflow:hidden}
.cat-hero::after{content:'';position:absolute;right:-30px;top:-30px;width:180px;height:180px;background:rgba(255,255,255,.05);border-radius:50%;pointer-events:none}
.cat-hero-title{font-size:22px;font-weight:900;margin-bottom:4px}
.cat-hero-sub{font-size:13px;opacity:.7}
.cat-filter-bar{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:20px}
.cat-filter-pill{padding:7px 14px;border-radius:22px;border:1.5px solid var(--border);background:#fff;font-size:13px;font-weight:600;cursor:pointer;color:var(--muted);font-family:inherit;transition:all .15s;display:flex;align-items:center;gap:6px}
.cat-filter-pill:hover{border-color:var(--g1);color:var(--g2)}
.cat-filter-pill.active{background:linear-gradient(135deg,var(--g1),var(--g2));border-color:transparent;color:#fff;box-shadow:0 3px 12px rgba(22,163,74,.3)}
.cat-section-title{font-size:13px;font-weight:800;color:var(--muted);text-transform:uppercase;letter-spacing:.8px;margin:20px 0 12px;display:flex;align-items:center;gap:8px}
.prod-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(155px,1fr));gap:12px;margin-bottom:24px}
.prod-card{background:#fff;border-radius:14px;border:1.5px solid var(--border);padding:16px 14px;cursor:pointer;transition:all .2s;position:relative;overflow:hidden}
.prod-card::before{content:'';position:absolute;inset:0;background:linear-gradient(135deg,transparent 60%,rgba(22,163,74,.04));pointer-events:none}
.prod-card:hover{border-color:var(--g1);box-shadow:0 4px 20px rgba(22,163,74,.15);transform:translateY(-2px)}
.prod-card-emoji{font-size:32px;margin-bottom:10px;display:block}
.prod-card-name{font-size:13px;font-weight:700;color:var(--text);line-height:1.35;margin-bottom:4px}
.prod-card-brand{font-size:11px;color:var(--muted);font-weight:500;margin-bottom:10px}
.prod-card-footer{display:flex;align-items:center;justify-content:space-between}
.score-pill{display:inline-flex;align-items:center;gap:3px;padding:3px 8px;border-radius:20px;font-size:11px;font-weight:800}
.score-good{background:#f0fdf4;color:var(--g1);border:1px solid #bbf7d0}
.score-ok{background:#fffbeb;color:#92400e;border:1px solid #fde68a}
.score-bad{background:#fef2f2;color:var(--red);border:1px solid #fecaca}
.prod-card-arrow{font-size:13px;color:var(--muted)}
.cat-empty{text-align:center;padding:48px 20px;color:var(--muted)}
.search-in-cat{width:100%;padding:11px 16px;border:1.5px solid var(--border);border-radius:11px;font-size:14px;font-family:inherit;outline:none;color:var(--text);background:#fff;margin-bottom:16px;transition:border .15s}
.search-in-cat:focus{border-color:var(--g1);box-shadow:0 0 0 3px rgba(22,163,74,.1)}
.search-in-cat::placeholder{color:#9ca3af}

`;

function Footer({ setPage }) {
  return (
    <div style={{ background: "#1a4a2e", padding: "20px 24px", marginTop: 40, textAlign: "center" }}>
      <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>NutriScan 🇮🇳</div>
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 12 }}>AI powered food health analyzer for India</div>
      <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
        <span onClick={() => setPage("privacy")} style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>Privacy Policy</span>
        <span onClick={() => setPage("terms")} style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>Terms of Use</span>
      </div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 12 }}>© 2026 NutriScan India. For informational purposes only.</div>
    </div>
  );
}

function HistoryPage({ onBack, onAnalyze }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchHistory(); }, []);

  async function fetchHistory() {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }
    const { data } = await supabase
      .from('analysis_history')
      .select('*')
      .eq('user_id', user.id)
      .order('analyzed_at', { ascending: false });
    setHistory(data || []);
    setLoading(false);
  }

  async function deleteOne(id) {
    await supabase.from('analysis_history').delete().eq('id', id);
    setHistory(p => p.filter(h => h.id !== id));
  }

  async function deleteAll() {
    if (!window.confirm("Delete all analysis history?")) return;
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('analysis_history').delete().eq('user_id', user.id);
    setHistory([]);
  }

  function timeAgo(date) {
    const diff = Date.now() - new Date(date);
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f2f5f2", fontFamily: "inherit" }}>
      <div style={{ background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={onBack} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>← Back</button>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 20 }}>🕓 Analysis History</div>
        </div>
        {history.length > 0 && (
          <button onClick={deleteAll} style={{ background: "rgba(239,68,68,0.3)", border: "1px solid rgba(239,68,68,0.5)", color: "#fff", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>🗑️ Clear All</button>
        )}
      </div>

      <div style={{ maxWidth: 600, margin: "24px auto", padding: "0 16px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#666" }}>Loading...</div>
        ) : history.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#666" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>No analysis history yet</div>
            <div style={{ fontSize: 14, marginTop: 4 }}>Analyze a product to see it here</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {history.map(h => (
              <div key={h.id} style={{ background: "#fff", borderRadius: 12, padding: 16, display: "flex", alignItems: "center", gap: 14, position: "relative", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                onClick={() => { onAnalyze(h.product_name); onBack(); }}>
                <div style={{ fontSize: 36 }}>{h.product_emoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{h.product_name}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{timeAgo(h.analyzed_at)}</div>
                </div>
                <div style={{ textAlign: "center", marginRight: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: 20, color: h.health_score >= 7 ? "#2d7a4f" : h.health_score >= 5 ? "#f59e0b" : "#ef4444" }}>{h.health_score || "?"}</div>
                  <div style={{ fontSize: 10, color: "#888" }}>score</div>
                </div>
                <button onClick={e => { e.stopPropagation(); deleteOne(h.id); }} style={{ background: "#fee2e2", border: "none", color: "#ef4444", width: 28, height: 28, borderRadius: "50%", cursor: "pointer", fontWeight: 700, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PrivacyPage({ onBack }) {
  return (
    <div style={{ minHeight: "100vh", background: "#f2f5f2", fontFamily: "inherit" }}>
      <div style={{ background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16 }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>← Back</button>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 20 }}>🔒 Privacy Policy</div>
      </div>
      <div style={{ maxWidth: 700, margin: "32px auto", padding: "0 16px" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 32 }}>
          <div style={{ fontSize: 13, color: "#888", marginBottom: 24 }}>Last updated: March 2026</div>
          {[
            ["1. Information We Collect", "We collect information you provide when creating an account including your name, email, age, weight, height, gender and activity level. We also collect product analysis history, barcode scan data and product search queries when you use our analyzer. When you scan a barcode, basic product information may be cached on our servers to improve response speed for future users."],
            ["2. How We Use Your Information", "Your personal information is used to personalize your experience, provide accurate BMR calculations and workout recommendations. We do not sell your personal information to any third party."],
            ["3. Product URL Analysis", "When you paste a product URL, our AI analyzes the publicly available product information and transforms it into health insights. This transformed data may be saved to improve recommendations for all users. By pasting a URL you confirm that you are accessing publicly available information and take responsibility for the URLs you submit. We are not responsible for the accuracy of information on third party websites."],
            ["4. AI Accuracy Disclaimer", "All product analysis, health scores and nutritional information is generated by AI and may contain inaccuracies or errors. We do not guarantee the accuracy of any nutritional information provided. Always verify nutritional details with the actual product label. NutriScan India is not responsible for decisions made based on AI generated content. Health scores provided by NutriScan are AI-estimated and are not certified nutritional ratings. This app does not provide medical advice and is not a substitute for professional dietary consultation. Results may not be suitable for people with specific medical conditions, allergies or dietary restrictions."],
            ["5. Data Storage", "Your personal data is stored securely on Supabase servers. We use Row Level Security to ensure only you can access your personal data. Product analysis cache data may be stored and used to improve recommendations for all users."],
            ["6. Third Party Services", "We use Anthropic Claude AI for product analysis, Supabase for database and authentication, Vercel for hosting and Render for our backend server. We source product data from Open Food Facts (openfoodfacts.org), available under the Open Database License (ODbL). We are not responsible for the accuracy of data sourced from Open Food Facts. Each of these services has their own privacy policy."],
            ["7. Data Deletion", "You can delete your account and all associated personal data at any time from the Account Info page. Product cache data may be retained to improve service for other users."],
            ["8. Cookies", "We use essential cookies for authentication only. We do not use advertising or tracking cookies."],
            ["9. Children's Privacy", "Our service is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13 please contact us immediately at nutriscan.india@gmail.com"],
            ["10. Contact", "For any privacy concerns please contact us at nutriscanindia.app@gmail.com. We reserve the right to update this policy at any time. Continued use of the app after changes means you accept the updated policy."]
          ].map(([title, content]) => (
            <div key={title} style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: "#1a4a2e" }}>{title}</div>
              <div style={{ fontSize: 14, color: "#444", lineHeight: 1.7 }}>{content}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TermsPage({ onBack }) {
  return (
    <div style={{ minHeight: "100vh", background: "#f2f5f2", fontFamily: "inherit" }}>
      <div style={{ background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16 }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>← Back</button>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 20 }}>📋 Terms of Use</div>
      </div>
      <div style={{ maxWidth: 700, margin: "32px auto", padding: "0 16px" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 32 }}>
          <div style={{ fontSize: 13, color: "#888", marginBottom: 24 }}>Last updated: March 2026</div>
          {[
            ["1. Acceptance of Terms", "By using NutriScan India you agree to these terms. If you do not agree please do not use our service."],
            ["2. Service Description", "NutriScan India is an AI powered food product health analyzer for Indian consumers. We provide health scores and nutritional insights for informational purposes only. Analysis is powered by Claude AI (Anthropic). Results are AI-generated and may not always be accurate."],
            ["3. Medical Disclaimer", "NutriScan India is not a medical service. Our health scores and nutritional analysis are for informational purposes only and should not be used as medical advice. Always consult a qualified healthcare professional before making any dietary or health decisions."],
            ["4. AI Generated Content", "Product analysis is generated by AI and may contain inaccuracies. We do not guarantee the accuracy of nutritional information. Always verify with the actual product label. NutriScan India is not liable for any decisions made based on AI generated content. Health scores are AI-estimated and are not certified nutritional ratings by any government or health authority including FSSAI."],
            ["5. URL Submission", "You are solely responsible for the URLs you submit for analysis. By submitting a URL you confirm you are accessing publicly available information. We are not responsible for content from third party websites. Do not submit URLs containing private, confidential or copyrighted content."],
            ["6. User Accounts", "You are responsible for maintaining the confidentiality of your account credentials. You must be at least 13 years old to create an account. You are responsible for all activity that occurs under your account."],
            ["7. Acceptable Use", "You may not use NutriScan India for any illegal purpose, to scrape or copy our data, to harm other users, or to submit false or misleading product information. You may not attempt to reverse engineer, hack or disrupt our service. You may not use automated bots or scripts to query our analyzer."],
            ["8. Intellectual Property", "All content, design and code of NutriScan India is our intellectual property. You may not copy or reproduce it without permission. Product information analyzed from third party sources remains the property of their respective owners. Product data sourced from Open Food Facts is available under the Open Database License (ODbL) and remains the property of Open Food Facts contributors."],
            ["9. Limitation of Liability", "NutriScan India is provided as is without any warranty of any kind. We are not liable for any direct, indirect, incidental or consequential damages arising from use of our service, reliance on AI generated health scores, nutritional inaccuracies, or any dietary or health decisions made based on our content. Our total liability to you shall not exceed the amount you paid us in the last 3 months."],
            ["10. Changes to Terms", "We may update these terms at any time. Continued use of the service constitutes acceptance of new terms."],
            ["11. Governing Law", "These terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction of courts in India."],
            ["12. Contact", "For any questions about these terms please contact us at nutriscanindia.app@gmail.com"]
          ].map(([title, content]) => (
            <div key={title} style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: "#1a4a2e" }}>{title}</div>
              <div style={{ fontSize: 14, color: "#444", lineHeight: 1.7 }}>{content}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LandingPage({ onGetStarted, installPrompt, isIOS, handleInstall }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f0f7f2",
      fontFamily: "'DM Sans', sans-serif",
      overflowX: "hidden",
      color: "#1a1a1a",
      margin: 0, padding: 0, width: "100%"
    }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&family=Instrument+Serif:ital@0;1&display=swap');

        body { margin: 0; padding: 0; background: #f0f7f2; }

        .landing-wrap * { box-sizing: border-box; }

        .mesh-bg {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 80% 50% at 20% 40%, rgba(45,122,79,0.12) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 80% 20%, rgba(16,185,129,0.08) 0%, transparent 50%),
            radial-gradient(ellipse 40% 60% at 60% 80%, rgba(26,74,46,0.08) 0%, transparent 50%);
          animation: meshMove 12s ease-in-out infinite alternate;
        }

        @keyframes meshMove {
          0% { transform: scale(1); }
          100% { transform: scale(1.08); }
        }

        .float-card { animation: floatUp 3s ease-in-out infinite alternate; }
        .float-card:nth-child(2) { animation-delay: 1s; }
        .float-card:nth-child(3) { animation-delay: 2s; }

        @keyframes floatUp {
          0% { transform: translateY(0px); }
          100% { transform: translateY(-10px); }
        }

        .marquee-track {
          display: flex;
          gap: 48px;
          animation: marquee 25s linear infinite;
          white-space: nowrap;
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .feature-card-l {
          transition: all 0.3s ease;
          background: #fff;
          border: 1px solid rgba(45,122,79,0.12);
          border-radius: 16px;
          padding: 24px;
        }
        .feature-card-l:hover {
          transform: translateY(-4px);
          border-color: rgba(45,122,79,0.4) !important;
          box-shadow: 0 12px 40px rgba(45,122,79,0.12);
        }

        .cta-btn-l {
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
        }
        .cta-btn-l::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          transform: translateX(-100%);
          transition: transform 0.4s ease;
        }
        .cta-btn-l:hover::after { transform: translateX(100%); }
        .cta-btn-l:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(45,122,79,0.4) !important; }

        .ghost-btn-l { transition: all 0.2s ease; }
        .ghost-btn-l:hover { background: rgba(45,122,79,0.08) !important; border-color: rgba(45,122,79,0.4) !important; }

        @keyframes pulsRing {
          0% { box-shadow: 0 0 0 0 rgba(45,122,79,0.4); }
          70% { box-shadow: 0 0 0 12px rgba(45,122,79,0); }
          100% { box-shadow: 0 0 0 0 rgba(45,122,79,0); }
        }
        .pulse { animation: pulsRing 2s infinite; }

        @media (max-width: 768px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .hide-mobile { display: none !important; }
        }
      `}</style>

      <div className="landing-wrap">

        {/* ── NAVBAR ── */}
        <nav style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 500,
          padding: "16px 40px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: scrolled ? "rgba(240,247,242,0.97)" : "rgba(240,247,242,0.8)",
          backdropFilter: "blur(20px)",
          transition: "all 0.3s ease",
          borderBottom: scrolled ? "1px solid rgba(45,122,79,0.15)" : "1px solid transparent"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36,
              background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)",
              borderRadius: 10, display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 18,
              boxShadow: "0 4px 16px rgba(45,122,79,0.3)"
            }}>🥗</div>
            <span style={{ fontWeight: 700, fontSize: 17, color: "#1a4a2e" }}>
              NutriScan <span style={{ fontSize: 13, opacity: 0.7 }}>🇮🇳</span>
            </span>
          </div>
          <button onClick={onGetStarted} className="cta-btn-l" style={{
            background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)",
            border: "none", color: "#fff", padding: "10px 22px",
            borderRadius: 8, fontWeight: 700, cursor: "pointer",
            fontSize: 14, fontFamily: "inherit",
            boxShadow: "0 4px 20px rgba(45,122,79,0.35)"
          }}>Sign In</button>
        </nav>

        {/* ── HERO ── */}
        <div style={{
          width: "100%", minHeight: "100vh",
          position: "relative", display: "flex",
          flexDirection: "column", alignItems: "center",
          justifyContent: "center", textAlign: "center",
          padding: "120px 32px 80px", overflow: "hidden"
        }}>
          <div className="mesh-bg" style={{ zIndex: 0 }} />

          {/* Grid lines */}
          <div style={{
            position: "absolute", inset: 0, zIndex: 0,
            backgroundImage: "linear-gradient(rgba(45,122,79,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(45,122,79,0.04) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
            pointerEvents: "none"
          }} />

          {/* Bottom fade */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0, height: 200,
            background: "linear-gradient(0deg, #f0f7f2 0%, transparent 100%)",
            zIndex: 2, pointerEvents: "none"
          }} />

          {/* Content */}
          <div style={{ position: "relative", zIndex: 3, maxWidth: 800 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "rgba(45,122,79,0.1)",
              border: "1px solid rgba(45,122,79,0.25)",
              padding: "6px 16px", borderRadius: 100,
              fontSize: 13, fontWeight: 600, color: "#1a4a2e",
              marginBottom: 32
            }}>
              <span style={{
                width: 7, height: 7, background: "#2d7a4f",
                borderRadius: "50%", display: "inline-block",
                animation: "pulsRing 2s infinite"
              }} />
              AI Powered · Made for India
            </div>

            <h1 style={{
              fontSize: "clamp(40px, 7vw, 80px)",
              fontWeight: 900, lineHeight: 1.05,
              letterSpacing: "-3px", marginBottom: 24,
              fontFamily: "'Instrument Serif', serif",
              color: "#0f2d1a"
            }}>
              Know what's really
              <span style={{
                display: "block",
                background: "linear-gradient(135deg, #1a4a2e 0%, #2d7a4f 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent"
              }}>inside your food</span>
            </h1>

            <p style={{
              fontSize: "clamp(16px, 2vw, 20px)",
              color: "#4a6b56",
              lineHeight: 1.7, marginBottom: 48, maxWidth: 560,
              margin: "0 auto 48px"
            }}>
              Scan any Indian packaged product. Get AI-powered health scores, full ingredient analysis and healthier alternatives — in seconds.
            </p>

            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={onGetStarted} className="cta-btn-l" style={{
                background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)",
                border: "none", color: "#fff",
                padding: "16px 36px", borderRadius: 12,
                fontWeight: 800, cursor: "pointer", fontSize: 16,
                fontFamily: "inherit",
                boxShadow: "0 8px 30px rgba(45,122,79,0.35)"
              }}>🚀 Get Started Free</button>
              <button onClick={onGetStarted} className="ghost-btn-l" style={{
                background: "#fff",
                border: "1px solid rgba(45,122,79,0.2)",
                color: "#1a4a2e", padding: "16px 32px",
                borderRadius: 12, fontWeight: 600,
                cursor: "pointer", fontSize: 16,
                fontFamily: "inherit"
              }}>📷 Scan a Product</button>
            </div>

            <div style={{
              marginTop: 32, color: "#7a9a85", fontSize: 13
            }}>
              No credit card required · 20 free credits · Cancel anytime
            </div>
            {(installPrompt || isIOS) && !window.matchMedia('(display-mode: standalone)').matches && (
              <div style={{ marginTop: 16 }}>
                <button onClick={handleInstall} style={{
                  display: "flex", alignItems: "center", gap: 8, margin: "0 auto",
                  background: "rgba(45,122,79,0.08)", border: "1px solid rgba(45,122,79,0.25)",
                  color: "#1a4a2e", padding: "10px 24px", borderRadius: 24,
                  fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "inherit"
                }}>📲 Install NutriScan as App</button>
              </div>
            )}
          </div>
        </div>

        {/* ── MARQUEE ── */}
        <div style={{
          padding: "20px 0", overflow: "hidden",
          borderTop: "1px solid rgba(45,122,79,0.1)",
          borderBottom: "1px solid rgba(45,122,79,0.1)",
          background: "#fff"
        }}>
          <div className="marquee-track">
            {[...Array(2)].map((_, i) => (
              <div key={i} style={{ display: "flex", gap: 48, alignItems: "center" }}>
                {["Parle-G", "Maggi", "Amul", "Britannia", "Haldiram's", "Patanjali", "ITC Sunfeast", "Nestlé", "Cadbury", "Kurkure", "Lay's", "Red Bull", "Bournvita", "Horlicks", "Dabur", "Yoga Bar", "Saffola", "Fortune"].map(brand => (
                  <span key={brand + i} style={{
                    color: "rgba(26,74,46,0.3)", fontSize: 14,
                    fontWeight: 600, letterSpacing: "0.5px"
                  }}>{brand}</span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ── HOW IT WORKS ── */}
        <div style={{ padding: "100px 40px", maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div style={{
              color: "#2d7a4f", fontWeight: 600, fontSize: 13,
              textTransform: "uppercase", letterSpacing: 3, marginBottom: 12
            }}>How It Works</div>
            <h2 style={{
              fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 800,
              letterSpacing: "-1.5px", fontFamily: "'Instrument Serif', serif",
              color: "#0f2d1a"
            }}>
              From scan to insight<br />
              <span style={{ color: "#7a9a85" }}>in under 10 seconds</span>
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            {[
              ["🔍", "01", "Search or Scan", "Type any product name or scan its barcode with your phone camera. No app download needed."],
              ["🤖", "02", "AI Analyzes", "Claude AI reads real ingredients and nutrition data to generate an accurate health score from 0-10."],
              ["✅", "03", "Make Better Choices", "See exactly what's harmful, get personalized advice and discover healthier alternatives instantly."]
            ].map(([emoji, num, title, desc]) => (
              <div key={num} className="feature-card-l">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                  <div style={{
                    width: 48, height: 48,
                    background: "rgba(45,122,79,0.1)",
                    border: "1px solid rgba(45,122,79,0.2)",
                    borderRadius: 14, display: "flex",
                    alignItems: "center", justifyContent: "center", fontSize: 22
                  }}>{emoji}</div>
                  <span style={{ color: "rgba(45,122,79,0.2)", fontSize: 28, fontWeight: 900 }}>{num}</span>
                </div>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10, color: "#0f2d1a" }}>{title}</div>
                <div style={{ color: "#4a6b56", fontSize: 15, lineHeight: 1.65 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── FEATURES ── */}
        <div style={{ padding: "0 40px 100px", maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div style={{
              color: "#2d7a4f", fontWeight: 600, fontSize: 13,
              textTransform: "uppercase", letterSpacing: 3, marginBottom: 12
            }}>Features</div>
            <h2 style={{
              fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 800,
              letterSpacing: "-1.5px", fontFamily: "'Instrument Serif', serif",
              color: "#0f2d1a"
            }}>Everything you need to<br />
              <span style={{ color: "#7a9a85" }}>eat smarter</span>
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
            {[
              ["📷", "Barcode Scanner", "Scan any product instantly"],
              ["🏥", "Health Score", "0-10 score from real data"],
              ["⚠️", "Harmful Ingredients", "Know what to avoid"],
              ["🥗", "Healthier Alternatives", "Better options suggested"],
              ["📊", "Nutrition Profile", "Full nutritional breakdown"],
              ["🎯", "Personalized Insights", "Based on your health"],
              ["⚡", "BMR Calculator", "Your daily calorie needs"],
              ["🍽️", "Food Log", "Track everything you eat"]
            ].map(([emoji, title, desc]) => (
              <div key={title} className="feature-card-l">
                <div style={{ fontSize: 26, marginBottom: 10 }}>{emoji}</div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 5, color: "#0f2d1a" }}>{title}</div>
                <div style={{ color: "#4a6b56", fontSize: 12, lineHeight: 1.5 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── PRICING ── */}
        <div style={{ padding: "0 40px 100px", maxWidth: 800, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div style={{
              color: "#2d7a4f", fontWeight: 600, fontSize: 13,
              textTransform: "uppercase", letterSpacing: 3, marginBottom: 12
            }}>Pricing</div>
            <h2 style={{
              fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 800,
              letterSpacing: "-1.5px", fontFamily: "'Instrument Serif', serif",
              color: "#0f2d1a"
            }}>Simple, transparent pricing</h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            <div style={{
              background: "#fff", border: "1px solid rgba(45,122,79,0.12)",
              borderRadius: 20, padding: 32
            }}>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4, color: "#0f2d1a" }}>Free</div>
              <div style={{ color: "#7a9a85", fontSize: 14, marginBottom: 24 }}>Perfect for getting started</div>
              <div style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-2px", marginBottom: 4, color: "#0f2d1a" }}>₹0</div>
              <div style={{ color: "#7a9a85", fontSize: 13, marginBottom: 28 }}>forever</div>
              <button onClick={onGetStarted} className="ghost-btn-l" style={{
                width: "100%", padding: "12px",
                background: "#f0f7f2",
                border: "1px solid rgba(45,122,79,0.2)",
                color: "#1a4a2e", borderRadius: 10,
                fontWeight: 600, cursor: "pointer",
                fontSize: 14, fontFamily: "inherit", marginBottom: 24
              }}>Get started free</button>
              {["20 free credits", "Full health analysis", "Barcode scanner", "Analysis history", "All calculators"].map(f => (
                <div key={f} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                  <span style={{ color: "#2d7a4f" }}>✓</span>
                  <span style={{ color: "#4a6b56", fontSize: 14 }}>{f}</span>
                </div>
              ))}
            </div>

            <div style={{
              background: "linear-gradient(135deg, #1a4a2e, #2d7a4f)",
              borderRadius: 20, padding: 32, position: "relative",
              boxShadow: "0 20px 60px rgba(45,122,79,0.25)"
            }}>
              <div style={{
                position: "absolute", top: -12, right: 20,
                background: "#fff", color: "#1a4a2e",
                fontSize: 11, fontWeight: 700,
                padding: "4px 12px", borderRadius: 20,
                textTransform: "uppercase", letterSpacing: 1
              }}>Most Popular</div>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4, color: "#fff" }}>Pro</div>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginBottom: 24 }}>For health-conscious users</div>
              <div style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-2px", marginBottom: 4, color: "#fff" }}>₹99</div>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, marginBottom: 28 }}>per month</div>
              <button onClick={onGetStarted} className="cta-btn-l" style={{
                width: "100%", padding: "12px",
                background: "#fff", border: "none",
                color: "#1a4a2e", borderRadius: 10,
                fontWeight: 700, cursor: "pointer",
                fontSize: 14, fontFamily: "inherit", marginBottom: 24
              }}>Upgrade to Pro</button>
              {["60 credits every month", "Personalized health insights", "Based on your health profile", "Ad-free experience", "Priority support", "Export as PDF"].map(f => (
                <div key={f} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                  <span style={{ color: "#4ade80" }}>✓</span>
                  <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── FINAL CTA ── */}
        <div style={{ padding: "0 40px 100px" }}>
          <div style={{
            maxWidth: 700, margin: "0 auto", textAlign: "center",
            background: "linear-gradient(135deg, #1a4a2e, #2d7a4f)",
            borderRadius: 28, padding: "64px 32px",
            boxShadow: "0 20px 60px rgba(45,122,79,0.25)"
          }}>
            <div style={{ fontSize: 52, marginBottom: 20 }}>🥗</div>
            <h2 style={{
              fontSize: "clamp(24px, 4vw, 42px)", fontWeight: 800,
              letterSpacing: "-1.5px", marginBottom: 16,
              fontFamily: "'Instrument Serif', serif", color: "#fff"
            }}>Start eating smarter today</h2>
            <p style={{
              color: "rgba(255,255,255,0.7)", fontSize: 16,
              marginBottom: 36, lineHeight: 1.6
            }}>
              Join Indians making smarter food choices.<br />Free to start. No credit card required.
            </p>
            <button onClick={onGetStarted} className="cta-btn-l" style={{
              background: "#fff", border: "none",
              color: "#1a4a2e", padding: "16px 44px",
              borderRadius: 14, fontWeight: 800,
              cursor: "pointer", fontSize: 17,
              fontFamily: "inherit",
              boxShadow: "0 8px 30px rgba(0,0,0,0.15)"
            }}>🚀 Get Started Free — 20 Credits</button>
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, marginTop: 16 }}>
              nutriscanindia.com · AI Powered · Made in India 🇮🇳
            </div>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div style={{
          borderTop: "1px solid rgba(45,122,79,0.1)",
          padding: "24px 40px", background: "#fff",
          display: "flex", justifyContent: "space-between",
          alignItems: "center", flexWrap: "wrap", gap: 12
        }}>
          <div style={{ color: "#7a9a85", fontSize: 13 }}>
            © 2026 NutriScan India. For informational purposes only.
          </div>
          <div style={{ display: "flex", gap: 24 }}>
            {["Privacy Policy", "Terms of Use"].map(link => (
              <span key={link} style={{
                color: "#7a9a85", fontSize: 13,
                cursor: "pointer", textDecoration: "underline"
              }}>{link}</span>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

  function BillingPage({ onBack }) {
  return (
    <div style={{ minHeight: "100vh", background: "#f2f5f2", fontFamily: "inherit" }}>
      <div style={{ background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16 }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>← Back</button>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 20 }}>💳 Plan & Billing</div>
      </div>
      <div style={{ maxWidth: 500, margin: "32px auto", padding: "0 16px" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 28, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div style={{ fontWeight: 800, fontSize: 18 }}>Current Plan</div>
            <span style={{ background: "#e8f5e9", color: "#1a4a2e", padding: "4px 12px", borderRadius: 20, fontSize: 13, fontWeight: 700 }}>FREE</span>
          </div>
          {[["🔍 Product Analysis", "5 per day"], ["🍽️ Food Log", "Unlimited"], ["⚡ BMR Calculator", "Unlimited"], ["🔥 Workout Calculator", "Unlimited"]].map(([feature, limit]) => (
            <div key={feature} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f0f0f0", fontSize: 14 }}>
              <span>{feature}</span>
              <span style={{ fontWeight: 600, color: "#1a4a2e" }}>{limit}</span>
            </div>
          ))}
        </div>
        <div style={{ background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)", borderRadius: 16, padding: 28, color: "#fff", textAlign: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 8 }}>⚡ Pro Plan</div>
          <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 20 }}>Unlimited analyses, priority support, advanced insights</div>
          <div style={{ fontWeight: 800, fontSize: 28, marginBottom: 4 }}>₹99<span style={{ fontSize: 14, fontWeight: 400 }}>/month</span></div>
          <button style={{ marginTop: 16, width: "100%", padding: 12, background: "#fff", color: "#1a4a2e", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
            🚀 Coming Soon
          </button>
        </div>
      </div>
    </div>
  );
}

function AccountPage({ user, setUser, onBack }) {
  const [form, setForm] = useState({ name: user?.name || "", age: user?.age || "", weight: user?.weight || "", height: user?.height || "", gender: user?.gender || "male", activity: user?.activity || "moderate" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  async function save() {
    setSaving(true);
    const { data: { user: authUser } } = await supabase.auth.getUser();
    await supabase.from('profiles').upsert({
      id: authUser.id,
      name: form.name,
      age: parseInt(form.age),
      weight: parseFloat(form.weight),
      height: parseFloat(form.height),
      gender: form.gender,
      activity: form.activity
    });
    setUser(p => ({ ...p, ...form }));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  async function deleteAccount() {
    if (!window.confirm("Are you sure? This will permanently delete your account and all data. This cannot be undone.")) return;
    if (!window.confirm("Last warning — delete account permanently?")) return;
    setDeleting(true);
    const { data: { user: authUser } } = await supabase.auth.getUser();
    await supabase.from('profiles').delete().eq('id', authUser.id);
    await supabase.auth.signOut();
    setUser(null);
    onBack();
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f2f5f2", fontFamily: "inherit" }}>
      <div style={{ background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16 }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>← Back</button>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 20 }}>👤 Account Info</div>
      </div>
      <div style={{ maxWidth: 500, margin: "32px auto", padding: "0 16px" }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 28, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Personal Information</div>
          {[["name", "Name"], ["age", "Age (years)"], ["weight", "Weight (kg)"], ["height", "Height (cm)"]].map(([k, lbl]) => (
            <div key={k} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: "#444" }}>{lbl}</div>
              <input value={form[k]} onChange={f(k)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #ddd", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>
          ))}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: "#444" }}>Gender</div>
              <select value={form.gender} onChange={f("gender")} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #ddd", fontSize: 14, fontFamily: "inherit" }}>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: "#444" }}>Activity</div>
              <select value={form.activity} onChange={f("activity")} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #ddd", fontSize: 14, fontFamily: "inherit" }}>
                <option value="sedentary">Sedentary</option>
                <option value="light">Light</option>
                <option value="moderate">Moderate</option>
                <option value="active">Active</option>
                <option value="veryactive">Very Active</option>
              </select>
            </div>
          </div>
          <button onClick={save} disabled={saving} style={{ width: "100%", padding: 12, background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
            {saving ? "Saving..." : saved ? "✓ Saved!" : "Save Changes"}
          </button>
        </div>

        <div style={{ background: "#fff", borderRadius: 16, padding: 28, border: "1.5px solid #fee2e2" }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: "#ef4444" }}>⚠️ Danger Zone</div>
          <div style={{ fontSize: 14, color: "#666", marginBottom: 16 }}>Permanently delete your account and all associated data. This action cannot be undone.</div>
          <button onClick={deleteAccount} disabled={deleting} style={{ width: "100%", padding: 12, background: "#fee2e2", color: "#ef4444", border: "1.5px solid #fecaca", borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
            {deleting ? "Deleting..." : "🗑️ Delete Account Permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// AUTH MODAL
// ─────────────────────────────────────────────────────────────
function AuthPage({ onAuth, onGuest, onPage }) {
 async function handleGoogleLogin() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
       redirectTo: "https://nutriscanindia.com/auth/callback",
        queryParams: {
          prompt: 'select_account'
        }
      }
    });
    if (error) console.error(error);
  }
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", age: "", weight: "", height: "", gender: "male", activity: "moderate" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  async function submit() {
    if (!form.email || !form.password) { setError("Email and password required"); return; }
    setLoading(true);
    setError("");
    try {
      if (tab === "signup") {
        const { data, error } = await supabase.auth.signUp({ email: form.email, password: form.password });
        if (error) { setError(error.message); setLoading(false); return; }
        const uid = data?.user?.id;
        if (uid) {
          await supabase.from('profiles').upsert({
            id: uid,
            name: form.name || form.email.split("@")[0],
            age: parseInt(form.age) || null,
            weight: parseFloat(form.weight) || null,
            height: parseFloat(form.height) || null,
            gender: form.gender,
            activity: form.activity,
            credits: 20,
            plan: 'free'
          });
        }
        const user = { name: form.name || form.email.split("@")[0], email: form.email, age: form.age, weight: form.weight, height: form.height, gender: form.gender, activity: form.activity, credits: 20, plan: 'free' };
        onAuth(user);
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
        if (error) { setError(error.message); setLoading(false); return; }
        const { data: profile } = await supabase.from('profiles').select('*').eq('id', data.user.id).single();
        const user = {
          name: profile?.name || form.email.split("@")[0],
          email: form.email,
          age: profile?.age, weight: profile?.weight,
          height: profile?.height, gender: profile?.gender,
          activity: profile?.activity,
          credits: profile?.credits ?? 20,
          plan: profile?.plan || 'free'
        };
        onAuth(user);
      }
    } catch (err) {
      setError("Something went wrong. Try again.");
    }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f0f7f2",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "flex-start",
      padding: "80px 16px 40px", fontFamily: "inherit",
      position: "fixed", inset: 0, zIndex: 600,
      overflowY: "auto"
    }}>
      {/* Back to landing */}
      <div style={{ position: "absolute", top: 20, left: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 32, height: 32,
            background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)",
            borderRadius: 8, display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: 16
          }}>🥗</div>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#1a4a2e" }}>NutriScan 🇮🇳</span>
        </div>
      </div>

     {/* Card */}
      <div style={{
        background: "#fff",
        border: "1px solid rgba(45,122,79,0.12)",
        borderRadius: 20, padding: "28px 20px",
        width: "100%", maxWidth: 420,
        boxShadow: "0 8px 40px rgba(45,122,79,0.1)"
      }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🥗</div>
          <div style={{ fontWeight: 800, fontSize: 20, color: "#0f2d1a" }}>
            {tab === "login" ? "Welcome back" : "Create your account"}
          </div>
          <div style={{ fontSize: 13, color: "#7a9a85", marginTop: 4 }}>
            {tab === "login" ? "Sign in to continue to NutriScan" : "Start analyzing Indian food products"}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", background: "#f0f7f2", borderRadius: 10, padding: 4, marginBottom: 24 }}>
          {["login", "signup"].map(t => (
            <button key={t} onClick={() => { setTab(t); setError(""); }} style={{
              flex: 1, padding: "9px", border: "none",
              borderRadius: 8, fontFamily: "inherit",
              fontWeight: 700, fontSize: 14, cursor: "pointer",
              background: tab === t ? "linear-gradient(135deg,#1a4a2e,#2d7a4f)" : "transparent",
              color: tab === t ? "#fff" : "#7a9a85",
              transition: "all 0.2s"
            }}>{t === "login" ? "Log In" : "Sign Up"}</button>
          ))}
        </div>

        {/* Name field for signup */}
        {tab === "signup" && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#1a4a2e" }}>Name</div>
            <input value={form.name} onChange={f("name")} placeholder="Your name"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1.5px solid rgba(45,122,79,0.2)", background: "#f9fdf9", color: "#0f2d1a", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
          </div>
        )}

        {/* Email */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#1a4a2e" }}>Email</div>
          <input value={form.email} onChange={f("email")} type="email" placeholder="you@email.com"
            style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1.5px solid rgba(45,122,79,0.2)", background: "#f9fdf9", color: "#0f2d1a", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* Password */}
        <div style={{ marginBottom: tab === "signup" ? 14 : 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#1a4a2e" }}>Password</div>
          <input value={form.password} onChange={f("password")} type="password" placeholder="••••••••"
            style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1.5px solid rgba(45,122,79,0.2)", background: "#f9fdf9", color: "#0f2d1a", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* Extra fields for signup */}
        {tab === "signup" && (
          <>
           <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
              {[["age", "Age"], ["weight", "Weight (kg)"], ["height", "Height (cm)"]].map(([k, lbl]) => (
                <div key={k}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#1a4a2e" }}>{lbl}</div>
                  <input type="number" value={form[k]} onChange={f(k)} placeholder="-"
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid rgba(45,122,79,0.2)", background: "#f9fdf9", color: "#0f2d1a", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#1a4a2e" }}>Gender</div>
                <select value={form.gender} onChange={f("gender")}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid rgba(45,122,79,0.2)", background: "#f9fdf9", color: "#0f2d1a", fontSize: 13, fontFamily: "inherit", outline: "none" }}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#1a4a2e" }}>Activity</div>
                <select value={form.activity} onChange={f("activity")}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid rgba(45,122,79,0.2)", background: "#f9fdf9", color: "#0f2d1a", fontSize: 13, fontFamily: "inherit", outline: "none" }}>
                  <option value="sedentary">Sedentary</option>
                  <option value="light">Light</option>
                  <option value="moderate">Moderate</option>
                  <option value="active">Active</option>
                  <option value="veryactive">Very Active</option>
                </select>
              </div>
            </div>
          </>
        )}

        {/* Error */}
        {error && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 14, textAlign: "center", background: "#fee2e2", padding: "8px 12px", borderRadius: 8 }}>{error}</div>}

        {/* Submit */}
        <button onClick={submit} disabled={loading} style={{
          width: "100%", padding: "14px",
          background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)",
          border: "none", color: "#fff", borderRadius: 10,
          fontWeight: 700, fontSize: 15, cursor: "pointer",
          fontFamily: "inherit", marginBottom: 16,
          boxShadow: "0 4px 16px rgba(45,122,79,0.3)",
          opacity: loading ? 0.7 : 1
        }}>{loading ? "Please wait..." : tab === "login" ? "Log In →" : "Create Account →"}</button>

        {/* Switch tab */}
        {tab === "login" && (
          <div style={{ textAlign: "center", marginBottom: 16, fontSize: 13, color: "#7a9a85" }}>
            Don't have an account?{" "}
            <span onClick={() => { setTab("signup"); setError(""); }} style={{ color: "#1a4a2e", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>Sign Up</span>
          </div>
        )}
        {tab === "signup" && (
          <div style={{ textAlign: "center", marginBottom: 16, fontSize: 13, color: "#7a9a85" }}>
            Already have an account?{" "}
            <span onClick={() => { setTab("login"); setError(""); }} style={{ color: "#1a4a2e", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>Log In</span>
          </div>
        )}

{/* Google Login */}
        <button onClick={handleGoogleLogin} style={{
          width: "100%", padding: "13px",
          background: "#fff",
          border: "1.5px solid rgba(0,0,0,0.15)",
          color: "#444", borderRadius: 10,
          fontWeight: 600, fontSize: 14, cursor: "pointer",
          fontFamily: "inherit", marginBottom: 12,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
        }}>
          <img src="https://www.google.com/favicon.ico" width={18} height={18} alt="Google" />
          Continue with Google
        </button>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: "rgba(45,122,79,0.12)" }} />
          <span style={{ color: "#7a9a85", fontSize: 12 }}>or</span>
          <div style={{ flex: 1, height: 1, background: "rgba(45,122,79,0.12)" }} />
        </div>

        {/* Continue as Guest */}
        <button onClick={onGuest} style={{
          width: "100%", padding: "13px",
          background: "#f0f7f2",
          border: "1.5px solid rgba(45,122,79,0.2)",
          color: "#1a4a2e", borderRadius: 10,
          fontWeight: 600, fontSize: 14, cursor: "pointer",
          fontFamily: "inherit", marginBottom: 20
        }}>Continue as Guest →</button>

        {/* Terms */}
        {tab === "signup" && (
          <div style={{ fontSize: 12, color: "#7a9a85", textAlign: "center", lineHeight: 1.6 }}>
           By signing up you agree to our{" "}
            <span onClick={() => onPage("terms")} style={{ color: "#1a4a2e", textDecoration: "underline", cursor: "pointer" }}>Terms of Use</span>
            {" "}and{" "}
            <span onClick={() => onPage("privacy")} style={{ color: "#1a4a2e", textDecoration: "underline", cursor: "pointer" }}>Privacy Policy</span>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  async function handleLogin() {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setError("Access denied"); return; }
    if (email !== process.env.REACT_APP_ADMIN_EMAIL) {
      await supabase.auth.signOut();
      setError("Access denied");
      return;
    }
    onSuccess();
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f2f5f2" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 360, boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
        <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 4 }}>🛡️ Admin Access</div>
        <div style={{ color: "#666", fontSize: 14, marginBottom: 24 }}>NutriScan Admin Panel</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Email</div>
          <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="admin@email.com" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #ddd", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Password</div>
          <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="••••••••" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #ddd", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
        </div>
        {error && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 14 }}>{error}</div>}
        <button onClick={handleLogin} style={{ width: "100%", padding: 12, background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>Login →</button>
      </div>
    </div>
  );
}
function AuthModal({ mode, onClose, onAuth }) {
  const [tab, setTab] = useState(mode);
  const [form, setForm] = useState({ name: "", email: "", password: "", age: "", weight: "", height: "", gender: "male", activity: "moderate" });
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  async function submit() {
    if (!form.email || !form.password) return;
    try {
      if (tab === "signup") {
       const { data, error } = await supabase.auth.signUp({
  email: form.email,
  password: form.password
});
if (error) { alert(error.message); return; }
const uid = data?.user?.id || data?.session?.user?.id;
if (uid) {
  await supabase.from('profiles').upsert({
    id: uid,
    name: form.name || form.email.split("@")[0],
    age: parseInt(form.age) || null,
    weight: parseFloat(form.weight) || null,
    height: parseFloat(form.height) || null,
    gender: form.gender,
    activity: form.activity
  });
}
const user = { name: form.name || form.email.split("@")[0], email: form.email, age: form.age, weight: form.weight, height: form.height, gender: form.gender, activity: form.activity };
onAuth(user);
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: form.email,
          password: form.password
        });
        if (error) { alert(error.message); return; }
       const { data: profile } = await supabase
  .from('profiles')
  .select('*')
  .eq('id', data.user.id)
  .single();

const user = {
  name: profile?.name || form.email.split("@")[0],
  email: form.email,
  age: profile?.age,
  weight: profile?.weight,
  height: profile?.height,
  gender: profile?.gender,
  activity: profile?.activity,
  credits: profile?.credits ?? 20,
  plan: profile?.plan || 'free'
};
onAuth(user);
      }
    } catch (err) {
      alert("Something went wrong. Try again.");
    }
  }

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", gap: 4, background: "#f2f5f2", borderRadius: 10, padding: 4, marginBottom: 22 }}>
          {["login", "signup"].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "9px", border: "none", borderRadius: 8, fontFamily: "inherit", fontWeight: 700, fontSize: 14, cursor: "pointer", background: tab === t ? "#fff" : "none", color: tab === t ? "var(--g1)" : "var(--muted)", boxShadow: tab === t ? "var(--sh)" : "none", transition: "all .15s" }}>
              {t === "login" ? "Log In" : "Sign Up"}
            </button>
          ))}
        </div>
        {tab === "signup" && (
          <div className="inp-group">
            <div className="inp-label">Name</div>
            <input className="inp" placeholder="Your name" value={form.name} onChange={f("name")} />
          </div>
        )}
        <div className="inp-group">
          <div className="inp-label">Email</div>
          <input className="inp" type="email" placeholder="you@email.com" value={form.email} onChange={f("email")} />
        </div>
        <div className="inp-group">
          <div className="inp-label">Password</div>
          <input className="inp" type="password" placeholder="••••••••" value={form.password} onChange={f("password")} />
        </div>
        {tab === "signup" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
              {[["age", "Age (yrs)"], ["weight", "Weight (kg)"], ["height", "Height (cm)"]].map(([k, lbl]) => (
                <div key={k}>
                  <div className="inp-label">{lbl}</div>
                  <input className="inp" type="number" placeholder="-" value={form[k]} onChange={f(k)} />
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <div className="inp-label">Gender</div>
                <select className="sel" value={form.gender} onChange={f("gender")}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <div className="inp-label">Activity</div>
                <select className="sel" value={form.activity} onChange={f("activity")}>
                  <option value="sedentary">Sedentary</option>
                  <option value="light">Light</option>
                  <option value="moderate">Moderate</option>
                  <option value="active">Active</option>
                  <option value="veryactive">Very Active</option>
                </select>
              </div>
            </div>
          </>
        )}
        <button className="btn-full" onClick={submit}>{tab === "login" ? "Log In →" : "Create Account →"}</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SEARCH PAGE
// ─────────────────────────────────────────────────────────────
function SearchPage({ products: PRODUCTS, externalQuery, onExternalUsed, user, setUser, setPage, setAuthModal }) {
  const [query, setQuery] = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
 const [urlInput, setUrlInput] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [showScanner, setShowScanner] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const wrapRef = useRef();
  const inputRef = useRef();

  // When a product card in Categories page is clicked, auto-analyze it
 useEffect(() => {
    if (externalQuery) {
      if (externalQuery.barcode) {
        setQuery(externalQuery.name);
        handleBarcodeScan(externalQuery.barcode);
      } else {
        setQuery(externalQuery.name || externalQuery);
        analyze(externalQuery.name || externalQuery);
      }
      onExternalUsed?.();
    }
  }, [externalQuery]);// eslint-disable-line

  // Fuzzy multi-word search across name, brand, tags, category
  const filtered = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return PRODUCTS.slice(0, 16);
    const words = q.split(/\s+/).filter(Boolean);
    return PRODUCTS.filter(p => {
      const haystack = [p.n, p.b, p.cat, ...(p.tags || [])].join(" ").toLowerCase();
      // Match if ALL query words are found somewhere in haystack
      return words.every(w => haystack.includes(w));
    });
  })();

  const grouped = filtered.reduce((acc, p) => {
    if (!acc[p.cat]) acc[p.cat] = [];
    acc[p.cat].push(p);
    return acc;
  }, {});

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

async function checkAndDeductCredit() {
  // Guest user
 if (!user) {
    setAuthModal("signup");
    return false;
  }

  // Logged in user - check credits
  if (user.credits <= 0) {
    setPage("billing");
    return false;
  }

  // Deduct credit from Supabase
  try {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    await supabase.from('profiles')
      .update({ credits: user.credits - 1 })
      .eq('id', authUser.id);
    setUser(p => ({ ...p, credits: p.credits - 1 }));
  } catch (_) {}

  return true;
}


async function analyzeLabelPhoto(file) {
    setError(""); setResult(null); setLoading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const allowed = await checkAndDeductCredit();
      if (!allowed) { setLoading(false); return; }

      const res = await fetch(`${API_URL}/api/analyze-label`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: base64, mediaType: file.type })
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || "Could not read label. Try scanning the barcode instead.");
        setLoading(false);
        return;
      }

     const result = normalizeHealthScore(extractJSON(data.text));
      if (!result?.name) {
        setError("Could not extract product info from image. Make sure the label is clearly visible.");
        setLoading(false);
        return;
      }

      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          await supabase.from('analysis_history').insert({
            user_id: authUser.id,
            product_name: result.name,
            product_emoji: result.emoji || "🍱",
            health_score: result.healthScore || null,
            analysis: result
          });
        }
      } catch (_) {}

      // Save to cache using product name as key
      if (result.name) {
        const cacheKey = `name_${result.name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`;
        try {
          await supabase.from('product_cache').upsert({
            product_key: cacheKey,
            analysis: { ...result, verified: true }
          });
          console.log('✓ Label scan saved to cache:', cacheKey);
        } catch (_) {}
      }

      setResult({ ...result, verified: true });
      setActiveTab("overview");
    } catch (e) {
      setError(e.message || "Label scan failed. Please try again.");
    }
    setLoading(false);
  }


 async function analyze(name) {
    if (!name?.trim()) return;
    setShowDrop(false); setError(""); setResult(null); setLoading(true);
    const cacheKey = `name_${name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`;
    try {

      // Step 1: Check cache first
      try {
        const { data: cached } = await supabase
          .from('product_cache')
          .select('analysis')
          .eq('product_key', cacheKey)
          .single();
        if (cached?.analysis) {
          console.log('✓ Cache hit:', cacheKey);
          setResult({ ...cached.analysis });
          setActiveTab("overview");
          setLoading(false);
          return;
        }
      } catch (_) {}

      // Step 2: Check products table for barcode
      let barcodeData = null;
      try {
        const q = name.trim().toLowerCase();
        const { data: dbProduct } = await supabase
          .from('products')
          .select('barcode, name, brand')
          .ilike('name', `%${q}%`)
          .not('barcode', 'is', null)
          .limit(1)
          .single();

        if (dbProduct?.barcode) {
          console.log('✓ Found barcode in DB:', dbProduct.barcode);
          const res = await fetch(`${API_URL}/api/barcode/${dbProduct.barcode}`);
          const data = await res.json();
          if (res.ok && !data.error) {
            barcodeData = data;
            console.log('✓ Got real data from Open Food Facts');
          }
        }
      } catch (_) {}

      // Step 3a: If barcode data found — analyze with real data (verified)
      if (barcodeData) {
        const prompt = `Analyze this Indian food product using REAL verified data from the product label below. Do NOT assume anything:

Product: ${barcodeData.name || name}
Brand: ${barcodeData.brand || ''}

REAL Ingredients: ${barcodeData.ingredients || 'Not available'}

REAL Nutrition per 100g:
- Calories: ${barcodeData.nutrition.calories} kcal
- Protein: ${barcodeData.nutrition.protein}g
- Carbs: ${barcodeData.nutrition.carbs}g
- Fat: ${barcodeData.nutrition.fat}g
- Sugar: ${barcodeData.nutrition.sugar}g
- Fiber: ${barcodeData.nutrition.fiber}g
- Salt: ${barcodeData.nutrition.salt}g
- Saturated Fat: ${barcodeData.nutrition.saturated}g

Based on this REAL data return compact JSON only:
{"name":"product name","brand":"brand name","emoji":"relevant emoji",
"healthScore":ACTUAL_SCORE,
"fssaiCertified":ACTUAL_BOOL,
"summary":"2-3 line summary for Indian consumers",
"ingredients":[{"name":"ingredient","status":"harmful|moderate|safe","reason":"reason under 60 chars"}],
"harmfulIngredients":[{"name":"additive","why":"why harmful","severity":"high|medium|low"}],
"alternatives":[{"name":"product","brand":"brand","emoji":"emoji","score":ACTUAL_SCORE,"reason":"why better"}],
"nutrition":{"servingSize":"100g","calories":ACTUAL_VALUE,
"macros":[{"name":"Protein","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Total Carbs","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Sugars","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Dietary Fiber","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Total Fat","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Saturated Fat","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Trans Fat","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Sodium","value":ACTUAL_VALUE,"unit":"mg","dv":ACTUAL_VALUE}],
"micros":[]}}`;

        const analysis = await callClaude(prompt, "", 1400, false, cacheKey);
        const finalResult = { ...analysis, verified: true };
        await supabase.from('product_cache').upsert({ product_key: cacheKey, analysis: finalResult });
        setResult(finalResult);
        setActiveTab("overview");
        setLoading(false);
        return;
      }

      // Step 3b: No barcode — fall back to Claude name search (unverified)
      const p1prompt = `Analyze the Indian food product "${name}". ` +
        `STRICT RULE: Only use real verified data from your training knowledge. ` +
        `Do NOT assume or guess ingredients. ` +
        `If you are not certain about exact ingredients clearly mention in summary that data may not be fully accurate. ` +
        `Calculate healthScore from 1-10 based on actual ingredients and nutrition — do not use example values. ` +
        `fssaiCertified should be true only if you are certain this product is FSSAI certified. ` +
        `trustifiedCertified should always be false unless you have specific knowledge it is certified. ` +
        `Return compact JSON only, keep reason/why under 60 chars: ` +
        `{"name":"exact product name with brand","brand":"brand name","emoji":"single emoji","healthScore":ACTUAL_SCORE,` +
        `"fssaiCertified":ACTUAL_BOOL,` +
        `"summary":"2 sentences about this specific product for Indian consumers",` +
        `"ingredients":[{"name":"exact ingredient from label","status":"harmful|moderate|safe","reason":"reason"}],` +
        `"harmfulIngredients":[{"name":"additive","why":"why harmful","severity":"high|medium|low"}],` +
        `"alternatives":[{"name":"product","brand":"brand","emoji":"emoji","score":ACTUAL_SCORE,"reason":"why better"}]}`;

    const part1 = normalizeHealthScore(await callClaude(p1prompt, "", 1400, false, cacheKey));

     // CALL 2: Nutrition profile
      const p2prompt = `Provide nutrition facts for the Indian food product "${name}" per 100g. ` +
        `STRICT RULE: Do NOT fetch from any website. Use only your training knowledge. ` +
        `Only include values you are confident about for this specific product. ` +
        `If unsure about exact values use approximate values based on similar Indian products. ` +
        `For micros only include nutrients commonly listed on Indian product labels. ` +
        `Calculate dv (daily value percentage) based on a standard 2000 kcal diet. ` +
        `Replace all ACTUAL_VALUE with real numbers. Do not copy example values. ` +
        `Return compact JSON only: ` +
        `{"servingSize":"100g","calories":ACTUAL_VALUE,` +
        `"macros":[{"name":"Protein","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},` +
        `{"name":"Total Carbs","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},` +
        `{"name":"Sugars","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},` +
        `{"name":"Dietary Fiber","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},` +
        `{"name":"Total Fat","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},` +
        `{"name":"Saturated Fat","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},` +
        `{"name":"Trans Fat","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},` +
        `{"name":"Sodium","value":ACTUAL_VALUE,"unit":"mg","dv":ACTUAL_VALUE}],` +
        `"micros":[ONLY_IF_KNOWN]}`;

      const part2 = await callClaude(p2prompt, "", 800, false);

     const allowed = await checkAndDeductCredit();
      if (!allowed) { setLoading(false); return; }
      setResult({ ...part1, nutrition: part2, verified: false });
      setActiveTab("overview");
    } catch (e) { setError(e.message || "Analysis failed. Please try again."); }
    setLoading(false);
  }

  async function handleBarcodeScan(barcode) {
 setShowScanner(false);
  setError("");
  setScanLoading(true);

  try {
    const cacheKey = `barcode_${barcode}`;
    const { data: cached } = await supabase
      .from('product_cache')
      .select('analysis')
      .eq('product_key', cacheKey)
      .single();

    if (cached?.analysis) {
      console.log('✓ Cache hit:', cacheKey);
      // Deduct credit for cache hit
      const allowed = await checkAndDeductCredit();
      if (!allowed) { setScanLoading(false); return; }
      setResult({ ...cached.analysis, verified: true });
      setScanLoading(false);
      return;
    }

    // No cache — check Open Food Facts first before deducting credit
    const res = await fetch(`${API_URL}/api/barcode/${barcode}`);
    const data = await res.json();

    if (!res.ok || data.error) {
      // Product not found — do NOT deduct credit
      setError("Product not found in database. Try searching by name.");
      setScanLoading(false);
      return;
    }

    // Product found — now deduct credit
    const allowed = await checkAndDeductCredit();
    if (!allowed) { setScanLoading(false); return; }

    setQuery(data.name);
   const prompt = `Analyze this Indian food product using REAL verified data from the product label below. Do NOT assume anything:

Product: ${data.name}
Brand: ${data.brand}
Barcode: ${barcode}

REAL Ingredients: ${data.ingredients || 'Not available'}

REAL Nutrition per 100g:
- Calories: ${data.nutrition.calories} kcal
- Protein: ${data.nutrition.protein}g
- Carbs: ${data.nutrition.carbs}g
- Fat: ${data.nutrition.fat}g
- Sugar: ${data.nutrition.sugar}g
- Fiber: ${data.nutrition.fiber}g
- Salt: ${data.nutrition.salt}g
- Saturated Fat: ${data.nutrition.saturated}g

Based on this REAL data return compact JSON only:
{"name":"product name","brand":"brand name","emoji":"relevant emoji",
"healthScore":ACTUAL_SCORE,
"fssaiCertified":ACTUAL_BOOL,
"summary":"2-3 line summary for Indian consumers",
"ingredients":[{"name":"ingredient","status":"harmful|moderate|safe","reason":"reason under 60 chars"}],
"harmfulIngredients":[{"name":"additive","why":"why harmful","severity":"high|medium|low"}],
"alternatives":[{"name":"product","brand":"brand","emoji":"emoji","score":ACTUAL_SCORE,"reason":"why better"}],
"nutrition":{"servingSize":"100g","calories":ACTUAL_VALUE,
"macros":[{"name":"Protein","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Total Carbs","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Sugars","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Dietary Fiber","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Total Fat","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Saturated Fat","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Trans Fat","value":ACTUAL_VALUE,"unit":"g","dv":ACTUAL_VALUE},
{"name":"Sodium","value":ACTUAL_VALUE,"unit":"mg","dv":ACTUAL_VALUE}],
"micros":[]}}`;

    const analysis = await callClaude(prompt, "", 1400, false);
   const finalResult = { ...analysis, nutrition: analysis.nutrition, verified: true, barcode };

    await supabase.from('product_cache').upsert({
      product_key: cacheKey,
      analysis: finalResult
    });

    setResult(finalResult);
    setActiveTab("overview");

  } catch (err) {
    setError("Something went wrong. Please try again.");
  }

  setScanLoading(false);
}

    const sc = s => s >= 7 ? "#16a34a" : s >= 4 ? "#d97706" : "#dc2626";
  const sl = s => s >= 7 ? "Good" : s >= 4 ? "Average" : "Poor";
  const stC = s => s === "safe" ? "#16a34a" : s === "moderate" ? "#d97706" : "#dc2626";
  const stB = s => s === "safe" ? "#f0fdf4" : s === "moderate" ? "#fffbeb" : "#fef2f2";
  const stBr = s => s === "safe" ? "#bbf7d0" : s === "moderate" ? "#fde68a" : "#fecaca";

  return (
    <div>
      {/* Hero */}
      {/* Hero — overflow:visible so dropdown escapes the card */}
      <div className="hero">
        {/* Decorative circles contained in their own clipped layer */}
        <div style={{position:"absolute",inset:0,borderRadius:18,overflow:"hidden",pointerEvents:"none",zIndex:0}}>
          <div style={{position:"absolute",top:-60,right:-60,width:240,height:240,background:"rgba(255,255,255,.06)",borderRadius:"50%"}}/>
          <div style={{position:"absolute",bottom:-40,right:40,width:140,height:140,background:"rgba(255,255,255,.04)",borderRadius:"50%"}}/>
        </div>
        <div style={{position:"relative",zIndex:1}}>
          <h1 className="hero-title">🔍 Food Product Analyzer</h1>
          <p className="hero-sub">500+ Indian products · Ingredients · Nutrition · Health Score · Alternatives</p>
        </div>
        {/* Search wrap at z:999, OUTSIDE the clipped decorative layer */}
        <div className="search-wrap" ref={wrapRef} style={{position:"relative",zIndex:50}}>
          <div className="search-box">
            <div style={{ flex: 1, position: "relative" }}>
              <input ref={inputRef} className="search-inp" value={query}
                onChange={e => { setQuery(e.target.value); setShowDrop(true); }}
                onFocus={() => setShowDrop(true)}
                onKeyDown={e => { if (e.key === "Enter" && query.trim()) analyze(query.trim()); if (e.key === "Escape") setShowDrop(false); }}
                placeholder="Type product name or brand (e.g. Maggi, Oreo, Lay's, paneer...)"
                autoComplete="off" spellCheck={false}
              />
              <button className="btn-analyze" onClick={() => analyze(query.trim())}
                style={{position:"absolute",right:0,top:0,bottom:0,padding:"0 18px",background:"#f59e0b",border:"none",borderRadius:"0 11px 11px 0",color:"#fff",fontWeight:800,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>
                Analyze →
              </button>
              {showDrop && filtered.length > 0 && (
                <div className="drop slide-down">
                  {query.trim() === "" && <div className="drop-header">✨ Popular Products — start typing to filter</div>}
                  {query.trim() !== "" && <div className="drop-header">{filtered.length} result{filtered.length !== 1 ? "s" : ""} — click to analyze</div>}
                  {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat}>
                      <div className="drop-cat">{cat}</div>
                      {items.map((p, i) => (
                        <div key={i} className="drop-row" onMouseDown={e => { e.preventDefault(); setQuery(p.n); setShowDrop(false); analyze(p.n); }}>
                          <span style={{ fontSize: 20, width: 26, textAlign: "center", flexShrink: 0 }}>{p.e}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: "#0d1f0f" }}>{p.n}</div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>{p.b}</div>
                          </div>
                          <span style={{ fontSize: 11, color: "#d1d5db" }}>↵</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  {query.trim() !== "" && filtered.length === 0 && (
                    <div style={{padding:"14px 16px",color:"#6b7280",fontSize:13}}>
                      No matches — press Enter or click Analyze to search anyway
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

<button
  onClick={() => setShowScanner(true)}
  style={{
    display: "flex", alignItems: "center", gap: 8,
    background: "linear-gradient(135deg,#1a4a2e,#2d7a4f)",
    border: "none", color: "#fff", padding: "12px 20px",
    borderRadius: 12, cursor: "pointer", fontWeight: 700,
    fontSize: 14, marginBottom: 12, width: "100%",
    justifyContent: "center", boxShadow: "0 4px 15px rgba(26,74,46,0.3)"
  }}>
  📷 Scan Barcode
</button>

     <div className="url-row">
            <label style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 10, padding: "10px 14px", cursor: "pointer", width: "100%",
              color: "#fff", fontSize: 13, fontWeight: 600
            }}>
              📸 Scan Product Label Photo
              <input type="file" accept="image/*" capture="environment"
                style={{ display: "none" }}
                onChange={e => { if (e.target.files[0]) analyzeLabelPhoto(e.target.files[0]); }} />
            </label>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="loading-box" style={{ marginBottom: 20 }}>
          <div className="spin" style={{ width: 38, height: 38, border: "4px solid #e5e7eb", borderTopColor: "var(--g1)", borderRadius: "50%", margin: "0 auto 14px" }} />
          <div style={{ fontWeight: 800, fontSize: 16 }}>Analyzing product...</div>
          <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>Checking ingredients, certifications & nutritional data</div>
        </div>
      )}
      {error && !loading && <div className="error-box">⚠️ {error}</div>}

      {/* Results */}
      {result && !loading && (
        <div className="fade-up">
          {!result.verified && (
            <div style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 14,
              fontSize: 13,
              color: "#92400e",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 8
            }}>
              ⚠️ Analysis based on AI knowledge. For accurate results scan the product barcode directly.
            </div>
          )}
          {/* Header */}
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="result-header">
              <div style={{ fontSize: 52, lineHeight: 1 }}>{result.emoji || "📦"}</div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 21, fontWeight: 900, lineHeight: 1.2 }}>{result.name}</div>
                <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 3, marginBottom: 12, fontWeight: 500 }}>{result.brand}</div>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  <span className={`badge ${result.fssaiCertified ? "badge-green" : "badge-red"}`}>{result.fssaiCertified ? "✅" : "❌"} FSSAI Certified</span>
                </div>
              </div>
              <div style={{ textAlign: "center", flexShrink: 0 }}>
                <div className="score-ring" style={{ borderColor: sc(result.healthScore), margin: "0 auto 6px" }}>
                  <span style={{ fontSize: 24, fontWeight: 900, color: sc(result.healthScore), lineHeight: 1 }}>{result.healthScore}</span>
                  <span style={{ fontSize: 9, color: sc(result.healthScore), fontWeight: 700 }}>/10</span>
                </div>
                <div style={{ fontSize: 12, fontWeight: 800, color: sc(result.healthScore) }}>{sl(result.healthScore)}</div>
                <div style={{ fontSize: 10, color: "var(--muted)" }}>Health Score</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            {[["overview","📋 Overview"],["ingredients","🧪 Ingredients"],["nutrition","📊 Nutrition"],["alternatives","💚 Alternatives"]].map(([t, lbl]) => (
              <button key={t} className={`tab ${activeTab === t ? "active" : ""}`} onClick={() => setActiveTab(t)}>{lbl}</button>
            ))}
          </div>

          {activeTab === "overview" && (
            <div className="fade-up">
              <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 13, padding: "16px 18px", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#1d4ed8", marginBottom: 6 }}>📋 Summary</div>
                <p style={{ fontSize: 14, color: "#1e3a5f", lineHeight: 1.75 }}>{result.summary}</p>
              </div>
              {result.harmfulIngredients?.length > 0 ? (
                <div className="card">
                  <div className="section-title">⚠️ Harmful Ingredients</div>
                  {result.harmfulIngredients.map((h, i) => (
                    <div key={i} className="harmful-block">
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                        <span style={{ fontSize: 14 }}>🚫</span>
                        <span style={{ fontWeight: 800, fontSize: 14, color: "var(--red)", flex: 1 }}>{h.name}</span>
                        <span className={`badge ${h.severity === "high" ? "badge-red" : h.severity === "medium" ? "badge-yellow" : "badge-gray"}`}>{(h.severity || "").toUpperCase()}</span>
                      </div>
                      <p style={{ fontSize: 13, color: "#7f1d1d", paddingLeft: 22, lineHeight: 1.6 }}>{h.why}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 13, padding: "14px 18px", display: "flex", gap: 12, alignItems: "center" }}>
                  <span style={{ fontSize: 22 }}>✅</span>
                  <div>
                    <div style={{ fontWeight: 800, color: "var(--g2)", fontSize: 14 }}>No major harmful ingredients</div>
                    <div style={{ fontSize: 12, color: "var(--g1)", marginTop: 2 }}>Product appears free of high-risk additives</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "ingredients" && (
            <div className="card fade-up">
              <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 8, flexWrap: "wrap" }}>
                <span className="section-title" style={{ margin: 0 }}>🧪 Full Ingredient List</span>
                <div style={{ display: "flex", gap: 5, marginLeft: "auto" }}>
                  {["safe","moderate","harmful"].map(s => <span key={s} className={`badge badge-${s === "safe" ? "green" : s === "moderate" ? "yellow" : "red"}`}>{s}</span>)}
                </div>
              </div>
              {(result.ingredients || []).map((ing, i) => (
                <div key={i} className="ing-row">
                  <div className="ing-dot" style={{ background: stC(ing.status) }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{ing.name}</div>
                    {ing.reason && <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 2 }}>{ing.reason}</div>}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 20, background: stB(ing.status), color: stC(ing.status), border: `1px solid ${stBr(ing.status)}`, flexShrink: 0, textTransform: "capitalize" }}>{ing.status}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === "nutrition" && result.nutrition && (
            <div className="fade-up">
              <div className="card" style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div className="section-title" style={{ margin: 0 }}>📊 Nutrition Facts</div>
                  <span className="badge badge-blue">Per {result.nutrition.servingSize}</span>
                </div>
                <div style={{ textAlign: "center", padding: "16px 0", borderBottom: "2px solid var(--border)", borderTop: "2px solid var(--border)", marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)" }}>CALORIES</div>
                  <div style={{ fontSize: 42, fontWeight: 900, color: "var(--g1)" }}>{result.nutrition.calories}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>kcal per serving</div>
                </div>
                <div style={{ fontWeight: 800, fontSize: 12, color: "var(--muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".8px" }}>Macronutrients</div>
                <table className="nut-table">
                  <thead><tr><th>Nutrient</th><th>Amount</th><th>% Daily Value</th></tr></thead>
                  <tbody>
                    {(result.nutrition.macros || []).map((m, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fafafa" : "#fff" }}>
                        <td className="nut-name" style={{ paddingLeft: m.name.startsWith("of which") ? 24 : 10 }}>{m.name}</td>
                        <td style={{ fontWeight: 600 }}>{m.value}{m.unit}</td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, height: 6, background: "#f3f4f6", borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ width: `${Math.min(m.dv, 100)}%`, height: "100%", background: m.dv > 20 ? "#dc2626" : m.dv > 10 ? "#d97706" : "#16a34a", borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", minWidth: 32 }}>{m.dv}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {result.nutrition.micros?.length > 0 && (
                  <>
                    <div style={{ fontWeight: 800, fontSize: 12, color: "var(--muted)", margin: "16px 0 8px", textTransform: "uppercase", letterSpacing: ".8px" }}>Micronutrients & Vitamins</div>
                    <table className="nut-table">
                      <thead><tr><th>Nutrient</th><th>Amount</th><th>% Daily Value</th></tr></thead>
                      <tbody>
                        {result.nutrition.micros.map((m, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#fafafa" : "#fff" }}>
                            <td className="nut-name">{m.name}</td>
                            <td style={{ fontWeight: 600 }}>{m.value}{m.unit}</td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ flex: 1, height: 6, background: "#f3f4f6", borderRadius: 3, overflow: "hidden" }}>
                                  <div style={{ width: `${Math.min(m.dv, 100)}%`, height: "100%", background: "#16a34a", borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", minWidth: 32 }}>{m.dv}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
                <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 12 }}>* % Daily Values based on 2000 kcal diet as per FSSAI guidelines</p>
              </div>
            </div>
          )}

          {activeTab === "alternatives" && (
            <div className="card fade-up">
              <div className="section-title">💚 Healthier Alternatives</div>
              {(result.alternatives || []).map((a, i) => (
                <div key={i} className="alt-card" onClick={() => { setQuery(a.name); analyze(a.name + " " + a.brand); setActiveTab("overview"); }}>
                  <span style={{ fontSize: 28, flexShrink: 0 }}>{a.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 14 }}>{a.name}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>{a.brand}</div>
                    {a.reason && <div style={{ fontSize: 12, color: "var(--g1)", marginTop: 3 }}>✓ {a.reason}</div>}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 900, color: "var(--g1)" }}>⭐ {a.score}/10</div>
                    <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>tap to analyze</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    {/* Empty */}
      {!result && !loading && !error && (
        <div style={{ padding: "16px 0 8px" }}>
          <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, textAlign: "center" }}>Try a popular product</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
            {["Maggi","Lay's Magic Masala","Oreo","Dairy Milk","Kellogg's Chocos","Kurkure","Amul Butter","Red Bull"].map(p => (
              <button key={p} className="quick-pill" onClick={() => { setQuery(p); analyze(p); }}
                style={{ padding: "7px 14px", borderRadius: 20, border: "1.5px solid var(--border)", background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: "var(--text)", transition: "all .15s" }}>
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      {scanLoading && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
          zIndex: 2000, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 16
        }}>
          <div style={{ fontSize: 48 }}>🔍</div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>
            Analyzing product...
          </div>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>
            Fetching real nutritional data
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CALORIE COUNTER
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// FOOD DATABASE — INDIA SPECIFIC, CATEGORIZED
// Values per 100g or 100ml unless noted
// ─────────────────────────────────────────────────────────────
const QUICK_FOODS = {

  "Fruits & Berries": [
    {e:"🍌",n:"Banana",cal:89,p:1.1,c:23,f:0.3,unit:"g"},
    {e:"🍎",n:"Apple",cal:52,p:0.3,c:14,f:0.2,unit:"g"},
    {e:"🥭",n:"Mango",cal:60,p:0.8,c:15,f:0.4,unit:"g"},
    {e:"🍊",n:"Orange",cal:47,p:0.9,c:12,f:0.1,unit:"g"},
    {e:"🍇",n:"Grapes",cal:69,p:0.7,c:18,f:0.2,unit:"g"},
    {e:"🍉",n:"Watermelon",cal:30,p:0.6,c:8,f:0.2,unit:"g"},
    {e:"🍑",n:"Papaya",cal:43,p:0.5,c:11,f:0.3,unit:"g"},
    {e:"🍍",n:"Pineapple",cal:50,p:0.5,c:13,f:0.1,unit:"g"},
    {e:"🍓",n:"Strawberry",cal:33,p:0.7,c:8,f:0.3,unit:"g"},
    {e:"🫐",n:"Blueberry",cal:57,p:0.7,c:14,f:0.3,unit:"g"},
    {e:"🍈",n:"Guava",cal:68,p:2.6,c:14,f:1,unit:"g"},
    {e:"🍐",n:"Pear",cal:57,p:0.4,c:15,f:0.1,unit:"g"},
    {e:"🍒",n:"Jamun (Black Plum)",cal:60,p:0.7,c:14,f:0.3,unit:"g"},
    {e:"🫒",n:"Chickoo (Sapota)",cal:83,p:0.4,c:20,f:1.1,unit:"g"},
    {e:"🥝",n:"Kiwi",cal:61,p:1.1,c:15,f:0.5,unit:"g"},
    {e:"🍋",n:"Lemon",cal:29,p:1.1,c:9,f:0.3,unit:"g"},
    {e:"🍅",n:"Pomegranate",cal:83,p:1.7,c:19,f:1.2,unit:"g"},
    {e:"🫐",n:"Amla (Indian Gooseberry)",cal:44,p:0.9,c:10,f:0.6,unit:"g"},
  ],

  "Vegetables": [
    {e:"🥔",n:"Potato",cal:77,p:2,c:17,f:0.1,unit:"g"},
    {e:"🍠",n:"Sweet Potato",cal:86,p:1.6,c:20,f:0.1,unit:"g"},
    {e:"🧅",n:"Onion",cal:40,p:1.1,c:9,f:0.1,unit:"g"},
    {e:"🧄",n:"Garlic",cal:149,p:6.4,c:33,f:0.5,unit:"g"},
    {e:"🍅",n:"Tomato",cal:18,p:0.9,c:3.9,f:0.2,unit:"g"},
    {e:"🥦",n:"Cauliflower (Gobi)",cal:25,p:2,c:5,f:0.3,unit:"g"},
    {e:"🥬",n:"Spinach (Palak)",cal:23,p:2.9,c:3.6,f:0.4,unit:"g"},
    {e:"🫛",n:"Peas (Matar)",cal:81,p:5.4,c:14,f:0.4,unit:"g"},
    {e:"🥕",n:"Carrot",cal:41,p:0.9,c:10,f:0.2,unit:"g"},
    {e:"🥒",n:"Cucumber",cal:16,p:0.7,c:3.6,f:0.1,unit:"g"},
    {e:"🌽",n:"Corn (Bhutta)",cal:86,p:3.3,c:19,f:1.4,unit:"g"},
    {e:"🫑",n:"Capsicum (Bell Pepper)",cal:31,p:1,c:6,f:0.3,unit:"g"},
    {e:"🍆",n:"Brinjal (Baingan)",cal:25,p:1,c:6,f:0.2,unit:"g"},
    {e:"🥬",n:"Methi (Fenugreek Leaves)",cal:49,p:4.4,c:6,f:0.9,unit:"g"},
    {e:"🌿",n:"Bottle Gourd (Lauki)",cal:15,p:0.6,c:3.4,f:0.1,unit:"g"},
    {e:"🥬",n:"Cabbage (Patta Gobi)",cal:25,p:1.3,c:6,f:0.1,unit:"g"},
    {e:"🌿",n:"Bitter Gourd (Karela)",cal:17,p:1,c:3.7,f:0.2,unit:"g"},
    {e:"🟢",n:"Lady Finger (Bhindi)",cal:33,p:1.9,c:7,f:0.2,unit:"g"},
  ],

  "Dairy": [
    {e:"🥛",n:"Full Fat Milk",cal:61,p:3.2,c:4.8,f:3.5,unit:"ml"},
    {e:"🥛",n:"Toned Milk",cal:45,p:3.5,c:4.8,f:1.5,unit:"ml"},
    {e:"🥛",n:"Skimmed Milk",cal:35,p:3.5,c:5,f:0.1,unit:"ml"},
    {e:"🫙",n:"Curd (Dahi)",cal:60,p:3.5,c:4.7,f:3.3,unit:"g"},
    {e:"🫙",n:"Low Fat Curd",cal:35,p:4,c:4,f:0.5,unit:"g"},
    {e:"🥛",n:"Greek Yogurt",cal:59,p:10,c:3.6,f:0.4,unit:"g"},
    {e:"🧀",n:"Paneer (Full Fat)",cal:265,p:18,c:3,f:20,unit:"g"},
    {e:"🧀",n:"Low Fat Paneer",cal:140,p:18,c:4,f:6,unit:"g"},
    {e:"🫙",n:"Ghee",cal:900,p:0,c:0,f:99,unit:"g"},
    {e:"🧈",n:"Butter",cal:717,p:0.9,c:0.1,f:81,unit:"g"},
    {e:"🫙",n:"Buttermilk (Chaas)",cal:40,p:3.3,c:4.8,f:0.9,unit:"ml"},
    {e:"🍦",n:"Whey (Paneer Water)",cal:25,p:0.9,c:5,f:0.4,unit:"ml"},
  ],

  "Soy Products": [
    {e:"🟤",n:"Soya Chunks (Raw)",cal:336,p:52,c:33,f:0.5,unit:"g"},
    {e:"🟤",n:"Soya Chunks (Cooked)",cal:112,p:17,c:11,f:0.2,unit:"g"},
    {e:"⬜",n:"Tofu (Firm)",cal:144,p:17,c:2.8,f:8.7,unit:"g"},
    {e:"⬜",n:"Tofu (Soft/Silken)",cal:55,p:5,c:2,f:3,unit:"g"},
    {e:"🟫",n:"Tempeh",cal:193,p:20,c:9,f:11,unit:"g"},
    {e:"🥛",n:"Soy Milk (Unsweetened)",cal:33,p:3.3,c:1.9,f:1.8,unit:"ml"},
    {e:"🫘",n:"Soybean (Cooked)",cal:173,p:17,c:10,f:9,unit:"g"},
  ],

  "Grains & Flours": [
    {e:"🫓",n:"Whole Wheat Roti (1 pc)",cal:71,p:2.7,c:14,f:0.4,unit:"g"},
    {e:"🌾",n:"Whole Wheat Atta",cal:341,p:13,c:70,f:1.7,unit:"g"},
    {e:"🍚",n:"Rice (Raw)",cal:365,p:7,c:80,f:0.5,unit:"g"},
    {e:"🍚",n:"Rice (Cooked)",cal:130,p:2.7,c:28,f:0.3,unit:"g"},
    {e:"🟡",n:"Suji (Semolina)",cal:360,p:13,c:73,f:1,unit:"g"},
    {e:"⬜",n:"Maida (Refined Flour)",cal:364,p:10,c:76,f:1,unit:"g"},
    {e:"🟡",n:"Besan (Chickpea Flour)",cal:387,p:22,c:58,f:6,unit:"g"},
    {e:"🌿",n:"Bajra (Pearl Millet)",cal:361,p:11,c:67,f:5,unit:"g"},
    {e:"🟤",n:"Jowar (Sorghum)",cal:349,p:11,c:73,f:2,unit:"g"},
    {e:"🌾",n:"Ragi (Finger Millet)",cal:336,p:7,c:72,f:1.5,unit:"g"},
    {e:"🌾",n:"Oats (Rolled)",cal:389,p:17,c:66,f:7,unit:"g"},
    {e:"🟤",n:"Daliya (Broken Wheat)",cal:342,p:12,c:73,f:1.5,unit:"g"},
    {e:"🟡",n:"Poha (Flattened Rice)",cal:333,p:7,c:76,f:0.6,unit:"g"},
    {e:"🫓",n:"Makki ki Roti (1 pc)",cal:80,p:2,c:17,f:1,unit:"g"},
  ],

  "Lentils & Legumes": [
    {e:"🟡",n:"Moong Dal (Cooked)",cal:105,p:7,c:19,f:0.4,unit:"g"},
    {e:"🟠",n:"Masoor Dal (Cooked)",cal:116,p:9,c:20,f:0.4,unit:"g"},
    {e:"🟡",n:"Toor Dal (Cooked)",cal:118,p:7,c:21,f:0.4,unit:"g"},
    {e:"⬛",n:"Urad Dal (Cooked)",cal:105,p:7.5,c:18,f:0.4,unit:"g"},
    {e:"🟤",n:"Chana Dal (Cooked)",cal:164,p:9,c:27,f:2.7,unit:"g"},
    {e:"🔴",n:"Rajma (Cooked)",cal:127,p:8.7,c:22,f:0.5,unit:"g"},
    {e:"🟤",n:"Chole / Kabuli Chana (Cooked)",cal:164,p:9,c:27,f:2.6,unit:"g"},
    {e:"🟤",n:"Kala Chana (Cooked)",cal:150,p:9,c:26,f:2.5,unit:"g"},
    {e:"🟢",n:"Whole Moong (Raw)",cal:347,p:24,c:63,f:1.2,unit:"g"},
    {e:"🟢",n:"Moong Sprouts (Raw)",cal:30,p:3,c:5.9,f:0.2,unit:"g"},
    {e:"🟢",n:"Moong Sprouts (Cooked)",cal:105,p:7,c:19,f:0.4,unit:"g"},
    {e:"🟤",n:"Chana Sprouts (Raw)",cal:120,p:8,c:20,f:2,unit:"g"},
    {e:"🟤",n:"Chana Sprouts (Cooked)",cal:164,p:9,c:27,f:2.6,unit:"g"},
    {e:"🫘",n:"Moth Beans / Matki (Cooked)",cal:126,p:9,c:22,f:0.6,unit:"g"},
    {e:"🟢",n:"Peas (Matar - Cooked)",cal:81,p:5.4,c:14,f:0.4,unit:"g"},
  ],

  "Dry Fruits & Seeds": [
    {e:"🥜",n:"Almonds (Badam)",cal:579,p:21,c:22,f:50,unit:"g"},
    {e:"🟤",n:"Cashews (Kaju)",cal:553,p:18,c:30,f:44,unit:"g"},
    {e:"🟤",n:"Walnuts (Akhrot)",cal:654,p:15,c:14,f:65,unit:"g"},
    {e:"🟤",n:"Pistachios (Pista)",cal:562,p:20,c:28,f:45,unit:"g"},
    {e:"🟤",n:"Raisins (Kishmish)",cal:299,p:3.1,c:79,f:0.5,unit:"g"},
    {e:"🟤",n:"Dates (Khajoor)",cal:277,p:1.8,c:75,f:0.2,unit:"g"},
    {e:"🟤",n:"Figs (Anjeer - Dried)",cal:249,p:3.3,c:64,f:0.9,unit:"g"},
    {e:"🌰",n:"Peanuts (Moongphali)",cal:567,p:26,c:16,f:49,unit:"g"},
    {e:"🟤",n:"Flaxseeds (Alsi)",cal:534,p:18,c:29,f:42,unit:"g"},
    {e:"⬜",n:"Chia Seeds",cal:486,p:17,c:42,f:31,unit:"g"},
    {e:"⬜",n:"Sunflower Seeds",cal:584,p:21,c:20,f:51,unit:"g"},
    {e:"⬜",n:"Pumpkin Seeds (Kaddu ke Beej)",cal:559,p:30,c:11,f:49,unit:"g"},
    {e:"⬜",n:"Sesame Seeds (Til)",cal:573,p:18,c:23,f:50,unit:"g"},
    {e:"🟤",n:"Fox Nuts (Makhana)",cal:347,p:9.7,c:77,f:0.1,unit:"g"},
    {e:"🟤",n:"Apricots (Khubani - Dried)",cal:241,p:3.4,c:63,f:0.5,unit:"g"},
  ],

  "Protein Powder": [
    {e:"🥤",n:"Whey Protein (1 scoop ~30g)",cal:120,p:24,c:3,f:2,unit:"g"},
    {e:"🥤",n:"Whey Isolate (1 scoop ~30g)",cal:110,p:27,c:1,f:0.5,unit:"g"},
    {e:"🥤",n:"Casein Protein (1 scoop ~33g)",cal:120,p:24,c:4,f:1,unit:"g"},
    {e:"🌱",n:"Plant Protein / Soy Protein (1 scoop ~30g)",cal:110,p:22,c:5,f:2,unit:"g"},
    {e:"🥚",n:"Egg White Protein (1 scoop ~30g)",cal:100,p:24,c:1,f:0,unit:"g"},
  ],

  "Eggs": [
    {e:"🥚",n:"Whole Egg (Boiled)",cal:155,p:13,c:1.1,f:11,unit:"g"},
    {e:"🥚",n:"Egg White (Boiled)",cal:52,p:11,c:0.7,f:0.2,unit:"g"},
    {e:"🟡",n:"Egg Yolk",cal:322,p:16,c:3.6,f:27,unit:"g"},
    {e:"🍳",n:"Omelette (2 eggs, no oil)",cal:154,p:20,c:2,f:7,unit:"g"},
    {e:"🍳",n:"Scrambled Eggs (2 eggs + milk)",cal:182,p:14,c:3,f:13,unit:"g"},
    {e:"🥚",n:"Egg Bhurji (2 eggs + oil + veggies)",cal:220,p:14,c:4,f:16,unit:"g"},
  ],
};

// ─────────────────────────────────────────────────────────────
// CALORIE PAGE — Full fix: hooks before early return, QtyControl
// extracted, useEffect deps, save error handling, AI JSON guard
// ─────────────────────────────────────────────────────────────

const PRESET_AMOUNTS = [25, 50, 100, 150, 200];
const CATEGORIES = Object.keys(QUICK_FOODS);

const GOAL_MODES = [
  { key: "cut",         label: "Cut",      emoji: "🔥", offset: -300 },
  { key: "maintenance", label: "Maintain", emoji: "⚖️",  offset: 0   },
  { key: "bulk",        label: "Bulk",     emoji: "💪", offset: +300 },
  { key: "custom",      label: "Custom",   emoji: "✏️",  offset: null },
];

function scaledFood(f, qty) {
  const r = qty / 100;
  return {
    ...f,
    qty,
    cal: Math.round(f.cal * r * 10) / 10,
    p:   Math.round(f.p   * r * 10) / 10,
    c:   Math.round(f.c   * r * 10) / 10,
    f:   Math.round(f.f   * r * 10) / 10,
    id:  Date.now() + Math.random(),
  };
}

// ── FIX: QtyControl moved OUTSIDE CaloriePage so React doesn't
// treat it as a new component type on every render (fixes input
// focus loss / remount bug).
function QtyControl({ f, selectedQty, setSelectedQty, customQty, setCustomQty }) {
  const fKey     = f.n;
  const qty      = selectedQty[fKey] ?? 100;
  const isLiquid = f.unit === "ml";
  const preview  = scaledFood(f, qty);
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
        {PRESET_AMOUNTS.map(p => (
          <button key={p}
            onClick={e => {
              e.stopPropagation();
              setSelectedQty(q => ({ ...q, [fKey]: p }));
              setCustomQty(q => ({ ...q, [fKey]: "" }));
            }}
            style={{
              padding: "2px 9px", borderRadius: 20, fontSize: 11, cursor: "pointer",
              border:     qty === p ? "2px solid var(--g1)" : "1px solid #d1d5db",
              background: qty === p ? "#f0fdf4" : "#fff",
              color:      qty === p ? "var(--g1)" : "#374151",
              fontWeight: qty === p ? 700 : 400,
            }}>
            {p}{isLiquid ? "ml" : "g"}
          </button>
        ))}
        <input
          type="number" min="1" max="2000"
          value={customQty[fKey] ?? ""}
          placeholder="custom"
          onClick={e => e.stopPropagation()}
          onChange={e => {
            const v = e.target.value;
            setCustomQty(q => ({ ...q, [fKey]: v }));
            if (+v > 0) setSelectedQty(q => ({ ...q, [fKey]: +v }));
          }}
          style={{ width: 64, padding: "2px 6px", borderRadius: 8, fontSize: 11, border: "1px solid #d1d5db", outline: "none" }}
        />
        <span style={{ fontSize: 11, color: "var(--muted)" }}>{isLiquid ? "ml" : "g"}</span>
        <span style={{ fontSize: 11, color: "var(--g1)", fontWeight: 700, marginLeft: 4 }}>
          → {preview.cal} kcal
        </span>
      </div>
    </div>
  );
}

function CaloriePage({ user, supabase }) {
  const today = new Date().toISOString().split("T")[0];

  // ══════════════════════════════════════════════════════
  // ── FIX: ALL hooks must come before any early return ──
  // ══════════════════════════════════════════════════════

  const TDEE = user?.weight && user?.height && user?.age
    ? Math.round(
        (user.gender === "female"
          ? (10 * +user.weight + 6.25 * +user.height - 5 * +user.age - 161)
          : (10 * +user.weight + 6.25 * +user.height - 5 * +user.age + 5))
        * { sedentary:1.2, light:1.375, moderate:1.55, active:1.725, veryactive:1.9 }[user.activity || "moderate"]
      )
    : 2000;

  const [meals,          setMeals]          = useState([]);
  const [goalMode,       setGoalMode]       = useState("maintenance");
  const [customGoal,     setCustomGoal]     = useState("");
  const [showGoalPop,    setShowGoalPop]    = useState(false);
  const [tab,            setTab]            = useState("log");
  const [activeMealId,   setActiveMealId]   = useState(null);
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0]);
  const [customQty,      setCustomQty]      = useState({});
  const [selectedQty,    setSelectedQty]    = useState({});
  const [search,         setSearch]         = useState("");
  const [aiFood,         setAiFood]         = useState(null);
  const [aiQty,          setAiQty]          = useState(100);
  const [loading,        setLoading]        = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [dbLoaded,       setDbLoaded]       = useState(false);

  const catScrollRef = useRef(null);
  const saveArmed    = useRef(false);  // ← add this

  // ── computed goal calories
  const modeObj = GOAL_MODES.find(m => m.key === goalMode);
  const GOAL    = goalMode === "custom"
    ? (+customGoal || TDEE)
    : TDEE + (modeObj?.offset ?? 0);

  // ── totals
  const totals = meals.flatMap(m => m.items).reduce(
    (a, f) => ({ cal: a.cal + f.cal, p: a.p + f.p, c: a.c + f.c, f: a.f + f.f }),
    { cal: 0, p: 0, c: 0, f: 0 }
  );

// ── SUPABASE load
console.log("CaloriePage render, user:", user, "supabase:", supabase);

useEffect(() => {
  if (!user?.email || !supabase) return;

  async function loadLog() {
    console.log("Load started, user:", user.email, "date:", today);

    const { data, error } = await supabase
      .from("food_logs")
      .select("*")
      .eq("user_id", user.email)
      .eq("log_date", today)
      .maybeSingle();

    console.log("Load result, data:", data, "error:", error);

    if (error) {
      console.error("Load error:", error.message);
    } else if (data) {
      setMeals(data.meals || []);
      setGoalMode(data.goal_mode || "maintenance");
      setCustomGoal(data.custom_goal || "");
    } else {
      console.log("No log found for today");
    }

    setDbLoaded(true);
    saveArmed.current = true;

    console.log("DB loaded, saveArmed set to true");
  }

  loadLog();
}, [user?.email, supabase, today]);


  // ══════════════════════════════════════════
  // ── Auth gate AFTER all hooks
  // ══════════════════════════════════════════
  if (!user) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 8 }}>Login Required</div>
        <div style={{ color: "var(--muted)", fontSize: 14, marginBottom: 20 }}>
          Food logging is available for logged-in users only.
        </div>
        <button className="btn-green" onClick={() => window.location.hash = "#login"}>
          Login / Sign Up
        </button>
      </div>
    );
  }

  // ── helpers
  function addMeal() {
    const id    = Date.now();
    const label = `Meal ${meals.length + 1}`;
    setMeals(m => [...m, { id, label, items: [] }]);
    setActiveMealId(id);
    setTab("add");
  }

  function addFoodToMeal(f) {
    if (!activeMealId) return;
    const qty  = selectedQty[f.n] ?? 100;
    const item = scaledFood(f, qty);
    setMeals(ms => ms.map(m =>
      m.id === activeMealId ? { ...m, items: [...m.items, item] } : m
    ));
  }

  function removeItem(mealId, itemId) {
    setMeals(ms => ms.map(m =>
      m.id === mealId ? { ...m, items: m.items.filter(i => i.id !== itemId) } : m
    ));
  }

  async function saveLog() {
  console.log("saveLog called, email:", user?.email);
  if (!user?.email || !supabase) {
    alert("Not logged in or supabase missing");
    return;
  }
  setSaving(true);
  try {
    const { error } = await supabase
      .from("food_logs")
      .upsert(
        {
          user_id:     user.email,
          log_date:    today,
          meals:       meals,
          goal_mode:   goalMode,
          custom_goal: +customGoal || null,
          updated_at:  new Date().toISOString(),
        },
        { onConflict: "user_id,log_date" }
      );
    if (error) {
      console.error("Save error:", error.message);
      alert("Save failed: " + error.message);
    } else {
      alert("✅ Saved!");
    }
  } catch (err) {
    console.error("Save error:", err);
  }
  setSaving(false);
}

function addAiFood() {
    if (!activeMealId || !aiFood) return;
    const item = scaledFood(
      { ...aiFood, e: aiFood.emoji || aiFood.e, n: aiFood.name || aiFood.n },
      aiQty
    );
    setMeals(ms => ms.map(m =>
      m.id === activeMealId ? { ...m, items: [...m.items, item] } : m
    ));
    setAiFood(null);
    setSearch("");
    setTab("log");
  }

  function scrollCats(dir) {
    if (catScrollRef.current)
      catScrollRef.current.scrollBy({ left: dir * 160, behavior: "smooth" });
  }


async function searchAI() {
    if (!search.trim()) return;

    // Only Pro or trial users can use AI search
    const trialEnd = user?.trial_end ? new Date(user.trial_end) : null;
    const inTrial = trialEnd && trialEnd > new Date();
    if (user?.plan !== 'pro' && !inTrial) {
      alert("AI Food Search is a Pro feature. Upgrade to Pro for ₹99/month.");
      return;
    }

    setLoading(true);
    setAiFood(null);
    try {
      const raw = await callClaude(
        `Nutrition per 100g/100ml for "${search}" (Indian food). ` +
        `Return only JSON, no explanation: {"emoji":"🍛","name":"food name","cal":200,"p":8,"c":30,"f":5,"unit":"g"}`
      );
      const text   = typeof raw === "string" ? raw : JSON.stringify(raw);
      const clean  = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setAiFood(parsed);
      setAiQty(100);
    } catch (e) {
      console.error("AI food parse error:", e);
    }
    setLoading(false);
  }
  // ════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════
  return (
    <div>

      {/* ── SUMMARY CARD */}
      <div className="card" style={{ marginBottom: 14 }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div className="section-title" style={{ marginBottom: 0 }}>📊 Today's Summary</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {saving && <span style={{ fontSize: 11, color: "var(--muted)" }}>saving…</span>}
            <button onClick={() => setShowGoalPop(p => !p)}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                background: "#f0fdf4", border: "1.5px solid var(--g1)",
                borderRadius: 20, padding: "4px 12px", cursor: "pointer",
                fontSize: 12, fontWeight: 700, color: "var(--g1)"
              }}>
              {modeObj?.emoji} {modeObj?.label}
              {goalMode !== "custom" && <span style={{ fontWeight: 500, color: "var(--muted)" }}> · {GOAL} kcal</span>}
              <span style={{ fontSize: 10 }}>▾</span>
            </button>
          </div>
        </div>

        {showGoalPop && (
          <div style={{
            background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
            padding: 14, marginBottom: 12, boxShadow: "0 4px 24px #0001"
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".7px" }}>
              Set Goal Mode
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {GOAL_MODES.map(m => (
                <button key={m.key} onClick={() => setGoalMode(m.key)}
                  style={{
                    padding: "6px 14px", borderRadius: 20, fontSize: 13, cursor: "pointer",
                    border:     goalMode === m.key ? "2px solid var(--g1)" : "1px solid #d1d5db",
                    background: goalMode === m.key ? "#f0fdf4" : "#fff",
                    color:      goalMode === m.key ? "var(--g1)" : "#374151",
                    fontWeight: goalMode === m.key ? 700 : 500,
                  }}>
                  {m.emoji} {m.label}
                  {m.offset !== null && m.offset !== 0 && (
                    <span style={{ fontSize: 11, color: "var(--muted)", marginLeft: 4 }}>
                      ({m.offset > 0 ? "+" : ""}{m.offset})
                    </span>
                  )}
                </button>
              ))}
            </div>
            {goalMode === "custom" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <input
                  type="number" className="inp" placeholder="Enter custom kcal goal"
                  value={customGoal}
                  onChange={e => setCustomGoal(e.target.value)}
                  style={{ flex: 1, fontSize: 13 }}
                />
                <span style={{ fontSize: 12, color: "var(--muted)" }}>kcal</span>
              </div>
            )}
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
              Your TDEE: <b>{TDEE} kcal</b>
              {goalMode !== "custom" && <> → Goal: <b style={{ color: "var(--g1)" }}>{GOAL} kcal</b></>}
              {goalMode === "custom" && customGoal && <> → Goal: <b style={{ color: "var(--g1)" }}>{GOAL} kcal</b></>}
            </div>
            <button onClick={() => setShowGoalPop(false)}
              style={{ marginTop: 10, width: "100%", padding: "7px", borderRadius: 10, background: "var(--g1)", color: "#fff", border: "none", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
              ✓ Done
            </button>
          </div>
        )}

        <div className="prog-wrap">
          <div className="prog-label">
            <span style={{ fontWeight: 700 }}>Calories</span>
            <span style={{ color: totals.cal > GOAL ? "var(--red)" : "var(--g1)", fontWeight: 800 }}>
              {Math.round(totals.cal)} / {GOAL} kcal
            </span>
          </div>
          <div className="prog-bar">
            <div className="prog-fill" style={{
              width: `${Math.min((totals.cal / GOAL) * 100, 100)}%`,
              background: totals.cal > GOAL ? "var(--red)" : "linear-gradient(90deg,var(--g1),var(--g2))"
            }} />
          </div>
        </div>

        <div className="macro-grid">
          {[
            ["cal","Calories","var(--g1)",Math.round(totals.cal),"kcal"],
            ["p","Protein","#2563eb",Math.round(totals.p),"g"],
            ["c","Carbs","#d97706",Math.round(totals.c),"g"],
            ["f","Fat","#dc2626",Math.round(totals.f),"g"],
          ].map(([k,lbl,col,val,u]) => (
            <div key={k} className="macro-box">
              <div className="macro-val" style={{ color: col }}>{val}</div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>{u}</div>
              <div className="macro-lbl">{lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TABS */}
      <div className="tabs">
        <button className={`tab ${tab === "log" ? "active" : ""}`} onClick={() => setTab("log")}>🍽️ Food Log</button>
        <button className={`tab ${tab === "add" ? "active" : ""}`} onClick={() => { if (activeMealId) setTab("add"); }} disabled={!activeMealId}>
          ➕ Add Food {activeMealId && <span style={{ fontSize: 11, opacity: .7 }}>({meals.find(m => m.id === activeMealId)?.label})</span>}
        </button>
      </div>

      {/* ══════════════ LOG TAB ══════════════ */}
      {tab === "log" && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div className="section-title" style={{ marginBottom: 0 }}>🍽️ Today's Meals</div>
           <div style={{ display:"flex", gap:8 }}>
  <button className="btn-green" onClick={saveLog} disabled={saving}>
    {saving ? "Saving..." : "💾 Save"}
  </button>
  <button className="btn-green" onClick={addMeal}>+ Add Meal</button>
</div>
          </div>

          {meals.length === 0 ? (
            <div className="empty" style={{ padding: "30px 0" }}>
              <div style={{ fontSize: 36 }}>🍽️</div>
              <div style={{ fontWeight: 700, marginTop: 10 }}>No meals logged yet</div>
              <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>Tap "+ Add Meal" to start logging</div>
              <button className="btn-green" style={{ marginTop: 12 }} onClick={addMeal}>+ Add Meal</button>
            </div>
          ) : meals.map(meal => {
            const mT = meal.items.reduce(
              (a, f) => ({ cal: a.cal + f.cal, p: a.p + f.p, c: a.c + f.c, f: a.f + f.f }),
              { cal: 0, p: 0, c: 0, f: 0 }
            );
            return (
              <div key={meal.id} style={{ marginBottom: 18 }}>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  background: "#f0fdf4", borderRadius: 10, padding: "8px 12px", marginBottom: 6
                }}>
                  <span style={{ fontWeight: 800, fontSize: 14, color: "var(--g1)" }}>🥗 {meal.label}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>
                      {Math.round(mT.cal)} kcal · P:{Math.round(mT.p)}g · C:{Math.round(mT.c)}g · F:{Math.round(mT.f)}g
                    </span>
                    <button onClick={() => { setActiveMealId(meal.id); setTab("add"); }}
                      style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, border: "1px solid var(--g1)", background: "#fff", color: "var(--g1)", cursor: "pointer", fontWeight: 700 }}>
                      + Add
                    </button>
                  </div>
                </div>
                {meal.items.length === 0
                  ? <div style={{ fontSize: 13, color: "var(--muted)", padding: "6px 4px" }}>No items yet.</div>
                  : meal.items.map(f => (
                    <div key={f.id} className="food-item">
                      <div style={{ fontSize: 20 }}>{f.e}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{f.n}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>
                          {f.qty}{f.unit} · P:{f.p}g C:{f.c}g F:{f.f}g
                        </div>
                      </div>
                      <div style={{ fontWeight: 800, color: "var(--g1)", fontSize: 13 }}>{f.cal} kcal</div>
                      <button onClick={() => removeItem(meal.id, f.id)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: 15, padding: 4, marginLeft: 4 }}>✕</button>
                    </div>
                  ))
                }
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════ ADD FOOD TAB ══════════════ */}
      {tab === "add" && (
        <div className="card">

          <div style={{
            background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10,
            padding: "8px 14px", marginBottom: 14, fontSize: 13, fontWeight: 700, color: "var(--g1)",
            display: "flex", justifyContent: "space-between", alignItems: "center"
          }}>
            <span>➕ Adding to: {meals.find(m => m.id === activeMealId)?.label}</span>
            <button onClick={() => setTab("log")}
              style={{ fontSize: 12, padding: "3px 10px", borderRadius: 20, border: "1px solid var(--g1)", background: "#fff", color: "var(--g1)", cursor: "pointer", fontWeight: 700 }}>
              ✓ Done
            </button>
          </div>

          {/* AI Search — Pro only */}
          {(() => {
            const trialEnd = user?.trial_end ? new Date(user.trial_end) : null;
            const inTrial = trialEnd && trialEnd > new Date();
            const canUseAI = user?.plan === 'pro' || inTrial;
            return (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <div className="section-title" style={{ marginBottom: 0 }}>🔍 Search Any Food</div>
                  {!canUseAI && <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>⭐ Pro</span>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input className="inp" style={{ flex: 1, opacity: canUseAI ? 1 : 0.5 }}
                    placeholder={canUseAI ? "e.g. samosa, curd rice, protein shake..." : "Upgrade to Pro to search any food"}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && searchAI()}
                    disabled={!canUseAI} />
                  <button onClick={searchAI} disabled={loading || !canUseAI}
                    style={{ padding: "10px 16px", borderRadius: 10, border: "none", fontWeight: 700, fontSize: 13, cursor: canUseAI ? "pointer" : "not-allowed", background: canUseAI ? "linear-gradient(135deg,var(--g1),var(--g2))" : "#e5e7eb", color: canUseAI ? "#fff" : "#9ca3af", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                    {loading ? "..." : canUseAI ? "Search" : "🔒 Pro"}
                  </button>
                </div>
                {loading && (
                  <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="spin" style={{ width: 16, height: 16, border: "3px solid #e5e7eb", borderTopColor: "var(--g1)", borderRadius: "50%" }} />
                    Looking up nutrition data...
                  </div>
                )}
                {aiFood && (
                  <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 11, padding: 14, marginTop: 12 }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <div style={{ fontSize: 32 }}>{aiFood.emoji || aiFood.e}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 15 }}>{aiFood.name || aiFood.n}</div>
                        <div style={{ fontSize: 12, color: "var(--muted)" }}>
                          per 100{aiFood.unit || "g"} · {aiFood.cal} kcal · P:{aiFood.p}g C:{aiFood.c}g F:{aiFood.f}g
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                      {PRESET_AMOUNTS.map(p => (
                        <button key={p} onClick={() => setAiQty(p)}
                          style={{
                            padding: "2px 9px", borderRadius: 20, fontSize: 11, cursor: "pointer",
                            border:     aiQty === p ? "2px solid var(--g1)" : "1px solid #d1d5db",
                            background: aiQty === p ? "#f0fdf4" : "#fff",
                            color:      aiQty === p ? "var(--g1)" : "#374151",
                            fontWeight: aiQty === p ? 700 : 400,
                          }}>
                          {p}{aiFood.unit || "g"}
                        </button>
                      ))}
                      <input type="number" min="1"
                        value={aiQty} onChange={e => setAiQty(+e.target.value || 100)}
                        style={{ width: 64, padding: "2px 6px", borderRadius: 8, fontSize: 11, border: "1px solid #d1d5db" }} />
                      <span style={{ fontSize: 12, color: "var(--g1)", fontWeight: 700 }}>
                        → {Math.round(aiFood.cal * aiQty / 100)} kcal
                      </span>
                    </div>
                    <button className="btn-green" style={{ marginTop: 10, width: "100%" }} onClick={addAiFood}>
                      + Add to {meals.find(m => m.id === activeMealId)?.label}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          <div className="divider" />

          {/* Category tabs with desktop scroll arrows */}
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 12 }}>
            <button onClick={() => scrollCats(-1)} className="cat-arrow"
              style={{
                display: "none", flexShrink: 0, width: 28, height: 28, borderRadius: "50%",
                border: "1px solid #d1d5db", background: "#fff", cursor: "pointer",
                fontSize: 14, alignItems: "center", justifyContent: "center",
              }}>‹</button>

            <div ref={catScrollRef}
              style={{
                flex: 1, overflowX: "auto", display: "flex", gap: 6,
                scrollbarWidth: "none", msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}>
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)}
                  style={{
                    whiteSpace: "nowrap", padding: "5px 13px", borderRadius: 20, fontSize: 12,
                    border:     activeCategory === cat ? "2px solid var(--g1)" : "1px solid #d1d5db",
                    background: activeCategory === cat ? "#f0fdf4" : "#fff",
                    color:      activeCategory === cat ? "var(--g1)" : "#374151",
                    fontWeight: activeCategory === cat ? 700 : 500,
                    cursor: "pointer", flexShrink: 0,
                  }}>
                  {cat}
                </button>
              ))}
            </div>

            <button onClick={() => scrollCats(1)} className="cat-arrow"
              style={{
                display: "none", flexShrink: 0, width: 28, height: 28, borderRadius: "50%",
                border: "1px solid #d1d5db", background: "#fff", cursor: "pointer",
                fontSize: 14, alignItems: "center", justifyContent: "center",
              }}>›</button>
          </div>

          <style>{`
            @media (hover: hover) and (pointer: fine) {
              .cat-arrow { display: flex !important; }
            }
          `}</style>

          {/* Food list */}
          {QUICK_FOODS[activeCategory].map((f, i) => (
            <div key={i} style={{ borderBottom: "1px solid #f3f4f6", paddingBottom: 10, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 22 }}>{f.e}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{f.n}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>per 100{f.unit}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontWeight: 800, color: "var(--g1)", fontSize: 13 }}>
                    {scaledFood(f, selectedQty[f.n] ?? 100).cal} kcal
                  </div>
                  <button onClick={() => addFoodToMeal(f)}
                    style={{
                      width: 28, height: 28, borderRadius: "50%", background: "var(--g1)",
                      color: "#fff", border: "none", cursor: "pointer",
                      fontSize: 18, fontWeight: 700,
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}>+</button>
                </div>
              </div>
              {/* Pass state down as props since QtyControl is now external */}
              <QtyControl
                f={f}
                selectedQty={selectedQty}
                setSelectedQty={setSelectedQty}
                customQty={customQty}
                setCustomQty={setCustomQty}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BMR CALCULATOR
// ─────────────────────────────────────────────────────────────
function BMRPage({ user }) {
  const ACT = { sedentary: { label: "Sedentary (desk job, no exercise)", f: 1.2 }, light: { label: "Lightly active (1-3 days/week)", f: 1.375 }, moderate: { label: "Moderate (3-5 days/week)", f: 1.55 }, active: { label: "Very active (6-7 days/week)", f: 1.725 }, veryactive: { label: "Athlete / 2x daily training", f: 1.9 } };
  const [form, setForm] = useState({ age: user?.age || "", weight: user?.weight || "", height: user?.height || "", gender: user?.gender || "male", activity: user?.activity || "moderate" });
  const [res, setRes] = useState(null);
  const [tips, setTips] = useState(""); const [tipsLoading, setTipsLoading] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  async function calc() {
    const { age, weight, height, gender, activity } = form;
    if (!age || !weight || !height) return;
    const bmr = gender === "male" ? (10 * +weight + 6.25 * +height - 5 * +age + 5) : (10 * +weight + 6.25 * +height - 5 * +age - 161);
    const tdee = Math.round(bmr * ACT[activity].f);
    setRes({ bmr: Math.round(bmr), tdee, cut: tdee - 500, bulk: tdee + 300 });
    setTipsLoading(true);
    try {
      const d = await callClaude(`Indian person: ${age}yr ${gender}, ${weight}kg, ${height}cm, ${activity} activity. BMR:${Math.round(bmr)}, TDEE:${tdee}. Give 3 practical Indian-diet tips as a JSON array: {"tips":["tip1","tip2","tip3"]}`);
      setTips(d.tips || []);
    } catch { }
    setTipsLoading(false);
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="section-title">⚡ Maintenance Calorie Calculator</div>
        <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 20 }}>Calculate your BMR & Total Daily Energy Expenditure using the Mifflin-St Jeor equation</p>
        {user?.weight && <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 13px", marginBottom: 16, fontSize: 13, color: "var(--g2)", fontWeight: 500 }}>✓ Pre-filled from your profile. Update if needed.</div>}
        <div className="calc-grid">
          {[["age","Age (years)","25"],["weight","Weight (kg)","70"],["height","Height (cm)","175"]].map(([k,lbl,ph]) => (
            <div key={k} className="inp-group">
              <div className="inp-label">{lbl}</div>
              <input className="inp" type="number" placeholder={ph} value={form[k]} onChange={f(k)} />
            </div>
          ))}
          <div className="inp-group">
            <div className="inp-label">Gender</div>
            <select className="sel" value={form.gender} onChange={f("gender")}><option value="male">Male</option><option value="female">Female</option></select>
          </div>
          <div className="inp-group" style={{ gridColumn: "span 2" }}>
            <div className="inp-label">Activity Level</div>
            <select className="sel" value={form.activity} onChange={f("activity")}>
              {Object.entries(ACT).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
        <button className="btn-full" onClick={calc}>⚡ Calculate My Calories</button>
      </div>

      {res && (
        <div className="fade-up">
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="section-title">📊 Your Results</div>
            <div className="result-boxes">
              {[["BMR","Base Metabolic Rate",res.bmr,"#f0fdf4","var(--g1)"],["TDEE","Maintenance Calories",res.tdee,"#eff6ff","var(--blue)"],["Cut","Fat Loss Target",res.cut,"#fffbeb","#d97706"],["Bulk","Muscle Gain Target",res.bulk,"#fef2f2","var(--red)"]].map(([lbl,sub,val,bg,col]) => (
                <div key={lbl} className="r-box" style={{ background: bg }}>
                  <div className="r-val" style={{ color: col }}>{val}</div>
                  <div style={{ fontSize: 10, color: col, fontWeight: 700 }}>kcal/day</div>
                  <div style={{ fontSize: 11, fontWeight: 800, marginTop: 4, color: col }}>{lbl}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{sub}</div>
                </div>
              ))}
            </div>
          </div>
          {(tips.length > 0 || tipsLoading) && (
            <div className="card">
              <div className="section-title">💡 Personalised Tips</div>
              {tipsLoading ? <div style={{ color: "var(--muted)", fontSize: 13 }}>Loading AI tips...</div> :
                tips.map((t, i) => <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, fontSize: 14, lineHeight: 1.6 }}><span style={{ color: "var(--g1)", fontWeight: 800, fontSize: 16 }}>✓</span>{t}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// WORKOUT CALORIE CALCULATOR
// ─────────────────────────────────────────────────────────────
const ACTIVITIES = [
  { cat: "Cardio", items: [
    { e: "🏃", n: "Running (moderate)", met: 9.8 },
    { e: "🚶", n: "Brisk Walking", met: 3.8 },
    { e: "🚴", n: "Cycling (moderate)", met: 7.5 },
    { e: "🏊", n: "Swimming (moderate)", met: 8.0 },
    { e: "⛹️", n: "Jump Rope", met: 12.3 },
    { e: "🕺", n: "Zumba / Dance", met: 6.5 },
  ]},
  { cat: "Strength", items: [
    { e: "🏋️", n: "Weight Training", met: 5.0 },
    { e: "💪", n: "Bodyweight Exercises", met: 4.5 },
    { e: "🤸", n: "CrossFit / HIIT", met: 10.0 },
    { e: "🧘", n: "Power Yoga", met: 4.0 },
  ]},
  { cat: "Sports", items: [
    { e: "⚽", n: "Football / Soccer", met: 10.3 },
    { e: "🏏", n: "Cricket", met: 4.5 },
    { e: "🏸", n: "Badminton", met: 7.0 },
    { e: "🎾", n: "Tennis", met: 8.0 },
    { e: "🏀", n: "Basketball", met: 8.3 },
  ]},
  { cat: "Leisure", items: [
    { e: "🧹", n: "Household Chores", met: 3.3 },
    { e: "🌿", n: "Gardening", met: 3.5 },
    { e: "🛒", n: "Shopping / Walking", met: 2.5 },
    { e: "🧘", n: "Yoga (gentle)", met: 2.5 },
  ]},
];

function WorkoutPage({ user }) {
  const [weight, setWeight] = useState(user?.weight || "");
  const [sel, setSel] = useState(null);
  const [duration, setDuration] = useState(30);
  const [result, setResult] = useState(null);

  function calc() {
    if (!sel || !weight) return;
    const w = +weight, d = +duration;
    const cal = Math.round((sel.met * w * d) / 60);
    setResult({ activity: sel.n, emoji: sel.e, duration: d, calories: cal, weight: w });
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="section-title">🔥 Workout Calorie Calculator</div>
        <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 16 }}>Select an activity, enter duration, and get calories burned based on your body weight using MET values.</p>
        <div className="inp-group">
          <div className="inp-label">Your Weight (kg)</div>
          <input className="inp" type="number" placeholder="e.g. 70" value={weight} onChange={e => setWeight(e.target.value)} style={{ maxWidth: 200 }} />
        </div>
        <div className="inp-label" style={{ marginBottom: 10 }}>Select Activity</div>
        {ACTIVITIES.map(grp => (
          <div key={grp.cat} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".8px" }}>{grp.cat}</div>
            <div className="activity-grid">
              {grp.items.map((a, i) => (
                <div key={i} className={`activity-card ${sel?.n === a.n ? "sel" : ""}`} onClick={() => { setSel(a); setResult(null); }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{a.e}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>{a.n}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 3 }}>MET: {a.met}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {sel && (
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 11, padding: "16px", marginTop: 6 }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 12 }}>{sel.e} {sel.n} — Set Duration</div>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <div>
                <div className="inp-label">Duration (minutes)</div>
                <input className="inp" type="number" value={duration} onChange={e => setDuration(e.target.value)} style={{ width: 120 }} />
              </div>
              <button className="btn-green" style={{ marginTop: 20 }} onClick={calc}>🔥 Calculate</button>
            </div>
          </div>
        )}
      </div>

      {result && (
        <div className="card fade-up">
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 52 }}>{result.emoji}</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 10 }}>{result.activity}</div>
            <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>{result.duration} minutes · {result.weight}kg</div>
            <div style={{ marginTop: 20, padding: "20px", background: "linear-gradient(135deg,#f0fdf4,#dcfce7)", borderRadius: 14 }}>
              <div style={{ fontSize: 52, fontWeight: 900, color: "var(--g1)" }}>{result.calories}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--g2)" }}>calories burned</div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 16, flexWrap: "wrap" }}>
              <span className="badge badge-blue">≈ {Math.round(result.calories / 100)} roti's worth</span>
              <span className="badge badge-yellow">≈ {Math.round(result.calories / 206)} cups of rice</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// function getScore(name) { return SCORES[name] ?? 5; }
function scoreColor(s) { return s >= 7 ? "#16a34a" : s >= 4 ? "#d97706" : "#dc2626"; }
function scoreClass(s) { return s >= 7 ? "score-good" : s >= 4 ? "score-ok" : "score-bad"; }
function scoreLabel(s) { return s >= 7 ? "✅ Healthy" : s >= 4 ? "⚠️ Moderate" : "❌ Avoid"; }

// All unique categories with metadata
const CAT_META = {
  "Biscuits":        { emoji: "🍪", desc: "Cookies, digestive, cream biscuits" },
  "Chips":           { emoji: "🍟", desc: "Chips, crisps & fried snacks" },
  "Namkeen":         { emoji: "🥨", desc: "Indian savory snacks & bhujia" },
  "Noodles":         { emoji: "🍜", desc: "Instant noodles & pasta" },
  "Instant":         { emoji: "🍚", desc: "Ready-mix breakfast & snacks" },
  "Chocolate":       { emoji: "🍫", desc: "Milk, dark & white chocolates" },
  "Cereals":         { emoji: "🥣", desc: "Oats, muesli & corn flakes" },
  "Paneer & Fresh Dairy": { emoji: "🧀", desc: "Paneer, curd & yogurt" },
  "Dairy":           { emoji: "🥛", desc: "Milk, cheese, butter & ghee" },
  "Health Drinks":   { emoji: "🫗", desc: "Horlicks, Bournvita, Milo" },
  "Juice":           { emoji: "🧃", desc: "Fruit juices & drinks" },
  "Soft Drinks":     { emoji: "🥤", desc: "Colas, sodas & energy drinks" },
  "Energy":          { emoji: "⚡", desc: "Energy & sports drinks" },
  "Protein":         { emoji: "💪", desc: "Whey protein & mass gainers" },
  "Protein Bars":    { emoji: "🍫", desc: "High protein snack bars" },
  "Spreads":         { emoji: "🥜", desc: "Peanut butter, honey & jams" },
  "Sauces":          { emoji: "🫙", desc: "Ketchup, mayo & chutneys" },
  "Oils":            { emoji: "🫗", desc: "Cooking oils & ghee" },
  "Bakery":          { emoji: "🍞", desc: "Bread, cakes & bakery items" },
  "Patanjali":       { emoji: "🌿", desc: "All Patanjali products" },
  "Health Snacks":   { emoji: "🌱", desc: "Nuts, seeds & healthy bars" },
  "Health & Ayurveda": { emoji: "🌿", desc: "Herbal & ayurvedic products" },
  "Tea & Coffee":    { emoji: "☕", desc: "Teas, coffees & hot drinks" },
  "Staples":         { emoji: "🌾", desc: "Atta, rice, pulses & grains" },
  "Ready to Eat":    { emoji: "🫕", desc: "Ready meals & instant food" },
  "Supplements":     { emoji: "💊", desc: "Vitamins & health supplements" },
  "Ice Cream":       { emoji: "🍦", desc: "Ice creams & frozen desserts" },
};

// ─────────────────────────────────────────────────────────────
// CATEGORIES PAGE
// ─────────────────────────────────────────────────────────────
function CategoriesPage({ products: PRODUCTS, initialCat, onAnalyze }) {
  const allCats = [...new Set(PRODUCTS.map(p => p.cat))];
  const [activeCat, setActiveCat] = useState(initialCat || "all");
  const [catSearch, setCatSearch] = useState("");
  const [sortBy, setSortBy] = useState("name"); // name | score-high | score-low


  const displayedProducts = PRODUCTS.filter(p => {
    const matchCat = activeCat === "all" || p.cat === activeCat;
    const q = catSearch.toLowerCase();
    const matchQ = !q || p.n.toLowerCase().includes(q) || p.b.toLowerCase().includes(q);
    return matchCat && matchQ;
  }).sort((a, b) => a.n.localeCompare(b.n));

  // Group by category when showing "all"
  const grouped = activeCat === "all"
    ? displayedProducts.reduce((acc, p) => { if (!acc[p.cat]) acc[p.cat] = []; acc[p.cat].push(p); return acc; }, {})
    : { [activeCat]: displayedProducts };

  const totalProducts = PRODUCTS.filter(p => activeCat === "all" || p.cat === activeCat).length;

  return (
    <div className="fade-up">
      {/* Hero */}
      <div className="cat-hero">
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="cat-hero-title">
            {activeCat === "all" ? "🛒 All Categories" : (CAT_META[activeCat]?.emoji || "📦") + " " + activeCat}
          </div>
          <div className="cat-hero-sub">
            {activeCat === "all"
              ? `${PRODUCTS.length} products across ${allCats.length} categories — discover healthier choices`
              : `${totalProducts} products · ${CAT_META[activeCat]?.desc || ""}`}
          </div>
        </div>
      </div>

      {/* Search within category */}
      <input
        className="search-in-cat"
        placeholder={"🔍 Search within " + (activeCat === "all" ? "all products" : activeCat) + "..."}
        value={catSearch}
        onChange={e => setCatSearch(e.target.value)}
      />

      {/* Sort + filter bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className={"cat-filter-pill " + (activeCat === "all" ? "active" : "")} onClick={() => setActiveCat("all")}>All</button>
          {allCats.map(c => (
            <button key={c} className={"cat-filter-pill " + (activeCat === c ? "active" : "")} onClick={() => setActiveCat(c)}>
              {CAT_META[c]?.emoji || "📦"} {c}
            </button>
          ))}
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ padding: "7px 12px", border: "1.5px solid var(--border)", borderRadius: 9, fontSize: 13, fontFamily: "inherit", background: "#fff", outline: "none", color: "var(--text)", cursor: "pointer" }}>
          <option value="name">Sort: A–Z</option>
          <option value="score-high">Sort: Healthiest first</option>
          <option value="score-low">Sort: Least healthy first</option>
        </select>
      </div>

      {/* Product grid */}
      {Object.entries(grouped).map(([cat, products]) => (
        <div key={cat}>
          {activeCat === "all" && (
            <div className="cat-section-title">
              <span>{CAT_META[cat]?.emoji || "📦"}</span>
              <span>{cat}</span>
              <span style={{ fontWeight: 500, color: "#b0bcb2", fontSize: 12 }}>({products.length})</span>
            </div>
          )}
          <div className="prod-grid">
            {products.map((p, i) => (
             <div key={i} className="prod-card" onClick={() => onAnalyze(p.n, p.barcode)}>
                <span className="prod-card-emoji">{p.e}</span>
                <div className="prod-card-name">{p.n}</div>
                <div className="prod-card-brand">{p.b}</div>
               <div className="prod-card-footer">
                  <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>Tap to analyze →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {displayedProducts.length === 0 && (
        <div className="cat-empty">
          <div style={{ fontSize: 44, marginBottom: 12 }}>🔍</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>No products found</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Try a different search or category</div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SIDEBAR DRAWER
// ─────────────────────────────────────────────────────────────
function SidebarDrawer({ products: PRODUCTS, open, onClose, page, setPage, user, setAuthModal }) {
  const [catsOpen, setCatsOpen] = useState(true);
  const [toolsOpen, setToolsOpen] = useState(false);
  const allCats = [...new Set(PRODUCTS.map(p => p.cat))];

  if (!open) return null;

  const navItems = [
    { id: "search", icon: "🔍", label: "Product Analyzer" },
    { id: "calorie", icon: "🍽️", label: "Food Log & Calorie Counter" },
    { id: "bmr", icon: "⚡", label: "BMR & Calorie Calculator" },
    { id: "workout", icon: "🔥", label: "Workout Calorie Burn" },
  ];

  function go(p) { setPage(p); onClose(); }

  return (
    <>
      <div className="sidebar-overlay" onClick={onClose} />
      <div className="sidebar-drawer">
        {/* Header */}
        <div className="drawer-head">
          <div className="drawer-logo">
            <div style={{ width: 28, height: 28, background: "linear-gradient(135deg,#16a34a,#22c55e)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🥗</div>
            <span style={{ fontWeight: 900, fontSize: 16, color: "#15803d" }}>NutriScan</span>
          </div>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>

        <div className="drawer-body">
          {/* User greeting */}
          {user ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 12px", background: "#f0fdf4", borderRadius: 10, marginBottom: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#16a34a,#f59e0b)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 14 }}>{user.name[0].toUpperCase()}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{user.name}</div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>{user.email}</div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, padding: "4px 4px 12px" }}>
              <button className="btn-outline" style={{ flex: 1, fontSize: 12 }} onClick={() => { setAuthModal("login"); onClose(); }}>Log In</button>
              <button className="btn-green" style={{ flex: 1, fontSize: 12 }} onClick={() => { setAuthModal("signup"); onClose(); }}>Sign Up</button>
            </div>
          )}

          {/* Categories accordion */}
          <div className="drawer-section">
            <button className={"drawer-section-btn " + (catsOpen ? "open" : "")} onClick={() => setCatsOpen(o => !o)}>
              <span><span className="drawer-section-icon">🛒</span>Categories</span>
              <span className={"drawer-chevron " + (catsOpen ? "open" : "")}>▶</span>
            </button>
            {catsOpen && (
              <div className="drawer-sub">
                <button className={"drawer-sub-item " + (page === "categories-all" ? "active" : "")}
                  onClick={() => go("categories-all")}>
                  <span>🌐</span><span>All Products</span>
                  <span className="sub-count">{PRODUCTS.length}</span>
                </button>
                {allCats.map(cat => {
                  const count = PRODUCTS.filter(p => p.cat === cat).length;
                  return (
                    <button key={cat} className={"drawer-sub-item " + (page === "cat-" + cat ? "active" : "")}
                      onClick={() => go("cat-" + cat)}>
                      <span>{CAT_META[cat]?.emoji || "📦"}</span>
                      <span style={{ flex: 1, textAlign: "left" }}>{cat}</span>
                      <span className="sub-count">{count}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>



          {/* Direct nav */}
          {navItems.map(n => (
            <button key={n.id} className={"drawer-nav-item " + (page === n.id ? "active" : "")} onClick={() => go(n.id)}>
              <span style={{ fontSize: 16 }}>{n.icon}</span>{n.label}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 16px", borderTop: "1px solid var(--border)", fontSize: 11, color: "var(--muted)", textAlign: "center" }}>
          🇮🇳 NutriScan · Indian Food Intelligence · AI Powered
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────
export default function App() {
 const [page, setPage] = useState(localStorage.getItem("currentPage") || "landing");
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [PRODUCTS, setProducts] = useState([]);

useEffect(() => {
  async function fetchProducts() {
    const { data } = await supabase
      .from('products')
      .select('*')
      .order('name');
    if (data) {
      setProducts(data.map(p => ({
        e: p.emoji,
        n: p.name,
        b: p.brand,
        cat: p.category,
        tags: p.tags || []
      })));
    }
  }
  fetchProducts();
}, []);
  const [user, setUser] = useState(null);
  const [authModal, setAuthModal] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
 const [showInstallBanner, setShowInstallBanner] = useState(false);
const [installPrompt, setInstallPrompt] = useState(null);
const [isIOS, setIsIOS] = useState(false);
 
    function go(p) {
    window.history.pushState({ page: p }, "", "");
    localStorage.setItem("currentPage", p);
    setPage(p);
  }

useEffect(() => {
    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    const standalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
    if (ios && !standalone) setIsIOS(true);

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    });
  }, []);

  function handleInstall() {
    if (installPrompt) {
      installPrompt.prompt();
      installPrompt.userChoice.then(() => {
        setInstallPrompt(null);
      });
    }
  }

  function handleInstall() {
    if (installPrompt) {
      installPrompt.prompt();
      installPrompt.userChoice.then(() => {
        setInstallPrompt(null);
        setShowInstallBanner(false);
      });
    }
  }

 useEffect(() => {
   if (window.location.hash && window.location.hash.includes('access_token')) {
  supabase.auth.getSession().then(() => {
    window.history.replaceState(null, "", window.location.pathname);
  });
} else if (window.location.hash) {
  window.history.replaceState(null, "", window.location.pathname);
}

    async function handleSession(session) {
  if (!session?.user) return;
  const { data: profile } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
  if (profile) {
    setUser({
      name: profile.name || session.user.email.split("@")[0],
      email: session.user.email,
      credits: profile.credits ?? 20,
      plan: profile.plan || 'free',
      age: profile.age, weight: profile.weight,
      height: profile.height, gender: profile.gender,
      activity: profile.activity
    });
    // ── ONLY redirect if on landing/auth, otherwise stay on current page
    setPage(p => (p === "landing" || p === "auth") ? "search" : p);
  } else {
    await supabase.from('profiles').upsert({
      id: session.user.id,
      name: session.user.user_metadata?.full_name || session.user.email.split("@")[0],
      credits: 20,
      plan: 'free'
    });
    setUser({
      name: session.user.user_metadata?.full_name || session.user.email.split("@")[0],
      email: session.user.email,
      credits: 20,
      plan: 'free'
    });
    // ── ONLY redirect if on landing/auth
    setPage(p => (p === "landing" || p === "auth") ? "search" : p);
  }
}

    // Handle existing session on page load
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session);
    });

    // Handle Google OAuth redirect and any future auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      handleSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // When a product card is clicked in Categories, jump to search and trigger analyze
  const [analyzeTarget, setAnalyzeTarget] = useState(null);
  
useEffect(() => {
  const handler = (e) => {
    if (e.ctrlKey && e.shiftKey && e.key === "A") setPage("admin");
  };
  window.addEventListener("keydown", handler);
  return () => window.removeEventListener("keydown", handler);
}, []);
// Browser back button support
function navigate(p) {
  setPage(p);
  localStorage.setItem("currentPage", p);
}

  useEffect(() => {
    window.history.replaceState({ page: "landing" }, "", "");
  }, []);

  useEffect(() => {
    const handlePop = (e) => {
      const p = e.state?.page;
      if (p) setPage(p);
      else setPage("landing");
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

 function handleCategoryAnalyze(productName, barcode) {
    setAnalyzeTarget({ name: productName, barcode: barcode || null });
    setPage("search");
  }

  // Determine which category page to show
  const isCatPage = page === "categories-all" || page.startsWith("cat-");
  const activeCatFromPage = page === "categories-all" ? "all" : page.startsWith("cat-") ? page.slice(4) : null;

  const navPages = [
  { id: "landing", label: "🏠 Home" },
  { id: "search",  label: "🔍 Analyzer" },
  { id: "calorie", label: "🍽️ Food Log" },
  { id: "bmr",     label: "⚡ BMR Calc" },
  { id: "workout", label: "🔥 Workout" },
];
  return (
    <>
      <style>{CSS}</style>

      {/* ── SIDEBAR DRAWER ── */}
      <SidebarDrawer
  products={PRODUCTS}
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  page={page}
  setPage={setPage}
  user={user}
  setAuthModal={setAuthModal}
/>

      {/* ── NAVBAR ── */}
      {page !== "landing" && <nav className="navbar">
        {/* Hamburger */}
        <button className="hamburger" onClick={() => setDrawerOpen(true)} title="Menu" style={{ marginRight: 12 }}>
          <div className="ham-line" />
          <div className="ham-line" />
          <div className="ham-line" />
        </button>

    <div className="nav-logo" onClick={() => go("landing")}>
  <div className="nav-logo-box">🥗</div>
  <span className="nav-logo-text">NutriScan</span>
  <span className="badge badge-green" style={{ fontSize: 10 }}>🇮🇳</span>
</div>
        <div className="nav-links">
          {navPages.map(p => (
            <button key={p.id} className={`nav-link ${page === p.id ? "active" : ""}`} onClick={() => go(p.id)}>{p.label}</button>
          ))}
          <button className={`nav-link ${isCatPage ? "active" : ""}`} onClick={() => setPage("categories-all")}>🛒 Categories</button>
        </div>

      <div className="nav-right" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
  {(installPrompt || isIOS) && !window.matchMedia('(display-mode: standalone)').matches && (
    <button onClick={handleInstall} className="hide-mobile" style={{
      padding: "6px 12px", borderRadius: 8, border: "1.5px solid var(--border)",
      background: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer",
      fontFamily: "inherit", color: "var(--text)", display: "flex", alignItems: "center", gap: 5
    }}> Install App</button>
  )}
          {user ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              <span className="hide-mobile" style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)", maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Hi, {user.name}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: user.credits <= 5 ? "#ef4444" : "#2d7a4f", background: user.credits <= 5 ? "#fee2e2" : "#e8f5e9", padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>{user.credits} cr</span>
             <div style={{ position: "relative" }}>
  <div className="user-avatar" onClick={() => setShowUserMenu(p => !p)}>{user.name[0].toUpperCase()}</div>
  {showUserMenu && (
    <div style={{ position: "absolute", right: 0, top: "110%", background: "#fff", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", minWidth: 200, zIndex: 500, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0" }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{user.name}</div>
        <div style={{ fontSize: 12, color: "#888" }}>{user.email}</div>
      </div>
      <div onClick={() => { go("account"); setShowUserMenu(false); }} style={{ padding: "12px 16px", cursor: "pointer", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>👤 Account Info</div>
      <div onClick={() => { go("history"); setShowUserMenu(false); }} style={{ padding: "12px 16px", cursor: "pointer", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>🕓 Analysis History</div>
      <div onClick={() => { go("billing"); setShowUserMenu(false); }} style={{ padding: "12px 16px", cursor: "pointer", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid #f0f0f0" }}>💳 Plan & Billing</div>
      <div onClick={async () => { await supabase.auth.signOut(); setUser(null); setShowUserMenu(false); setPage("search");}} style={{ padding: "12px 16px", cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#ef4444", display: "flex", alignItems: "center", gap: 8 }}>🚪 Log Out</div>
    </div>
  )}
</div>
            </div>
          ) : (
            <>
             <button className="btn-green" onClick={() => go("auth")}>Sign In</button>
            </>
          )}
        </div>
      </nav>}

     {/* ── PAGE CONTENT ── */}
<div style={{ maxWidth: 860, margin: "0 auto", padding: "24px 14px 100px", overflowX: "hidden" }}>
 {page === "landing" && <LandingPage onGetStarted={() => go("auth")} installPrompt={installPrompt} isIOS={isIOS} handleInstall={handleInstall} />}
  {page === "auth" && <AuthPage onAuth={u => { setUser(u); go("search"); }} onGuest={() => go("search")} onPage={go} />}
  {page === "privacy" && <PrivacyPage onBack={() => go("search")} />}
  {page === "terms" && <TermsPage onBack={() => go("search")} />}
  {page === "history" && <HistoryPage onBack={() => go("search")} onAnalyze={(name) => { setAnalyzeTarget(name); go("search"); }} />}
  {page === "account" && <AccountPage user={user} setUser={setUser} onBack={() => go("search")} />}
  {page === "billing" && <BillingPage onBack={() => go("search")} />}
        {page === "admin" && (
  isAdminLoggedIn
    ? <AdminPage onExit={() => { setPage("search"); setIsAdminLoggedIn(false); }} />
    : <AdminLogin onSuccess={() => setIsAdminLoggedIn(true)} />
)}
        {page === "search" && <SearchPage products={PRODUCTS} externalQuery={analyzeTarget} onExternalUsed={() => setAnalyzeTarget(null)} user={user} setUser={setUser} setPage={setPage} setAuthModal={setAuthModal} />}
       {page === "calorie" && <CaloriePage user={user} supabase={supabase} />}
        {page === "bmr"     && <BMRPage user={user} />}
        {page === "workout" && <WorkoutPage user={user} />}
        {isCatPage && <CategoriesPage products={PRODUCTS} initialCat={activeCatFromPage} onAnalyze={handleCategoryAnalyze} />}
      </div>

{showInstallBanner && window.innerWidth <= 768 && (page === "landing" || page === "auth") && (
        <div style={{
          position: "fixed",
          bottom: 0,
          left: 0, right: 0,
          background: "#fff",
          borderTop: "1px solid var(--border)",
          padding: "12px 16px",
          display: "flex", alignItems: "center",
          justifyContent: "center",
          zIndex: 998,
          boxShadow: "0 -2px 12px rgba(0,0,0,0.08)"
        }}>
          <span onClick={handleInstall} style={{
            fontSize: 13, fontWeight: 700, color: "var(--g1)", cursor: "pointer",
            textAlign: "center"
          }}>Install NutriScan India as an app</span>
          <button onClick={() => setShowInstallBanner(false)} style={{
            fontSize: 16, color: "#888", cursor: "pointer",
            background: "none", border: "none", padding: "4px 8px",
            lineHeight: 1, position: "absolute", right: 12
          }}>✕</button>
        </div>
      )}

{/* ── MOBILE BOTTOM NAV ── */}
      {page !== "landing" && page !== "auth" && (
        <div className="bottom-nav">
          {[
            { id: "search", icon: "🔍", label: "Analyzer" },
            { id: "calorie", icon: "🍽️", label: "Food Log" },
            { id: "bmr", icon: "⚡", label: "BMR" },
            { id: "workout", icon: "🔥", label: "Workout" },
          ].map(item => (
            <button key={item.id}
              className={`bottom-nav-item ${page === item.id || (item.id === "categories-all" && isCatPage) ? "active" : ""}`}
              onClick={() => go(item.id)}>
              <span className="bn-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    {(page === "landing" || page === "auth") && <Footer setPage={go} />}
     {page !== "landing" && page !== "auth" && <div style={{ height: 80 }} />}
      {authModal && (
        <AuthModal mode={authModal} onClose={() => setAuthModal(null)} onAuth={u => { setUser(u); setAuthModal(null); }} />
      )}
    </>
  );
}
